import { useState, useEffect, useRef } from 'react';
import { User, Users, Bell, Briefcase, Upload, X, CreditCard, Crown, RefreshCw, FileText, Palette, Sun, Moon, AlertTriangle, Trash2, FolderOpen, File, Calendar, Download, Eye, Sparkles, Shield, Phone, Mail, Key, Check, Loader, MessageSquare, Plus, Edit2, Save, Crop, ZoomIn, ZoomOut, RotateCcw, Beaker, Package, DollarSign, Warehouse } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import TeamManagement from './TeamManagement';
import PaymentSettings from './PaymentSettings';
import SubscriptionSettings from './SubscriptionSettings';
import MessagingSettings from './MessagingSettings';
import { useModal } from './AppModal';
import { useTheme, accentColors } from './ThemeContext';
import { useFeatures } from './FeatureContext';
import { isOfflineModeEnabled, setOfflineModeEnabled, warmCache, clearCache, getCacheSize, getCacheSummary } from './offlineCache';
import JSZip from 'jszip';
import ChemicalManagement from './ChemicalManagement';

export default function Settings({ user, userProfile, onProfileUpdate, companyId, userRole, onNavigate, onCompanyUpdate, initialSection, isMobile }) {
  const { showAlert, showConfirm, showSuccess, showError } = useModal();
  const { theme, colors, toggleMode, setAccentColor, isDark } = useTheme();
  const { company: featureCompany } = useFeatures();


  // Check if company is Team+ tier (team or higher) for Tech Permissions
  const isTeamPlus = ['team', 'business', 'growth', 'enterprise', 'unlimited'].includes(featureCompany?.tier);

  // Tech Permissions state
  const [techPermissions, setTechPermissions] = useState({
    tech_quoting_mode: 'flexible',
    tech_can_add_manual_items: true,
    tech_can_modify_prices: true,
    tech_max_discount_percent: 100,
    tech_can_create_service_plans: false
  });
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Message Templates state
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all'); // 'all', 'sms', 'email'
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    message: '',
    channel: 'sms',
    category: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const templateCategories = [
    'Appointment',
    'Quote',
    'Invoice',
    'Follow-up',
    'Reminder',
    'Thank You',
    'Other'
  ];

  const templateVariables = [
    { var: '{customer_name}', desc: 'Customer\'s first name' },
    { var: '{customer_full_name}', desc: 'Customer\'s full name' },
    { var: '{company_name}', desc: 'Your company name' },
    { var: '{appointment_time}', desc: 'Scheduled appointment time' },
    { var: '{appointment_date}', desc: 'Scheduled appointment date' },
    { var: '{address}', desc: 'Customer address' },
    { var: '{job_number}', desc: 'Job/Work order number' },
    { var: '{quote_total}', desc: 'Quote total amount' },
    { var: '{invoice_total}', desc: 'Invoice total amount' }
  ];
  const [activeTab, setActiveTab] = useState(
    initialSection === 'payment' ? 'payments' :
    initialSection === 'billing' || initialSection === 'subscription' ? 'subscription' :
    initialSection === 'referral' ? 'referral' : 'account'
  );
  const [generatingTerms, setGeneratingTerms] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [showTermsQuestionnaire, setShowTermsQuestionnaire] = useState(false);
  const [termsQuestionnaireAnswers, setTermsQuestionnaireAnswers] = useState({
    cancellation_notice_hours: 24,
    early_termination_fee: false,
    early_termination_fee_amount: '',
    refund_policy: 'prorated',
    warranty_period_months: 12,
    satisfaction_guarantee: true,
    reservice_policy: 'free',
    auto_renewal: true,
    custom_flavor: '' // Custom instructions for AI
  });
  const [companyInfo, setCompanyInfo] = useState({ name: '', business_type: '', state: '' });
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    default_view: 'home',
    menu_order: null,
    logo_url: null,
    sales_tax_mode: 'optional', // 'required', 'optional', 'disabled'
    recurring_enabled: false,
    advanced_quoting_enabled: false,
    // Quote customization
    quote_company_name: '',
    quote_company_address: '',
    quote_company_phone: '',
    quote_company_email: '',
    quote_custom_terms: '',
    quote_custom_footer: '',
    quote_show_logo: true,
    quote_terms_mode: 'optional_on', // 'required', 'optional_on', 'optional_off', 'disabled'
    completion_signature_mode: 'optional', // 'required', 'optional', 'disabled'
    // Smart Assistant
    smart_assistant_enabled: true,
    // Calendar settings
    calendar_slot_minutes: 60, // 15, 30, or 60
    business_hours_start: '08:00',
    business_hours_end: '17:00',
    // Branding colors
    brand_primary_color: '#4ecca3',
    brand_secondary_color: '#F05A28',
    brand_accent_color: '#3498db',
    brand_text_color: '#333333',
    // Quote fee settings
    quote_fee_enabled: false,
    default_quote_fee_amount: 75.00,
    // Auto-email receipts
    auto_email_receipts: true,
    // Field worker title (customizable per industry)
    field_worker_title: 'Technician',
    // Auto-bill recurring
    auto_bill_recurring: false,
    auto_bill_receipt_mode: 'always',
    // Invoice template settings
    invoice_payment_terms: 'Net 30',
    invoice_footer_text: '',
    invoice_notes: '',
    // Customer auto-archive
    auto_archive_enabled: false,
    auto_archive_inactive_days: 365,
    auto_archive_lost_leads: false,
    auto_archive_lost_lead_days: 90,
    // Inventory settings
    inventory_enabled: false,
    inventory_auto_deduct: true,
    inventory_low_stock_alerts: true,
    inventory_mode: 'simple',
    inventory_alert_email_digest: 'none',
    inventory_digest_emails: [],
    inventory_monthly_budget: 0,
    inventory_sms_critical_alerts: false,
    inventory_default_warning_threshold: 1.5,
    inventory_default_critical_threshold: 0.5
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Logo crop modal state
  const [showLogoCropModal, setShowLogoCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropScale, setCropScale] = useState(1);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cropCanvasRef = useRef(null);
  const cropImageRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveStatusTimerRef = useRef(null);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Recovery contacts state
  const [recoveryContacts, setRecoveryContacts] = useState([]);
  const [loadingRecovery, setLoadingRecovery] = useState(false);
  const [newRecoveryPhone, setNewRecoveryPhone] = useState('');
  const [newRecoveryEmail, setNewRecoveryEmail] = useState('');
  const [recoveryPhoneError, setRecoveryPhoneError] = useState('');
  const [recoveryEmailError, setRecoveryEmailError] = useState('');
  const [addingRecoveryContact, setAddingRecoveryContact] = useState(false);
  const [verifyingContact, setVerifyingContact] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [generatingCodes, setGeneratingCodes] = useState(false);

  // Offline mode state
  const [offlineEnabled, setOfflineEnabled] = useState(isOfflineModeEnabled());
  const [cacheWarming, setCacheWarming] = useState(false);
  const [cacheProgress, setCacheProgress] = useState({ current: 0, total: 0, table: '' });
  const [cacheSummary, setCacheSummary] = useState(null);
  const [cacheSize, setCacheSize] = useState('0');

  // Data export state
  const [exporting, setExporting] = useState(false);

  // Referral earnings state
  const [referralCredits, setReferralCredits] = useState([]);
  const [referredCompanies, setReferredCompanies] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  // Document storage state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('insurance');
  const [docExpirationDate, setDocExpirationDate] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const docInputRef = useRef(null);

  const documentTypes = [
    { id: 'insurance', label: 'Proof of Insurance', icon: '🛡️' },
    { id: 'ein', label: 'EIN Document', icon: '📋' },
    { id: 'license', label: 'Business License', icon: '📜' },
    { id: 'bond', label: 'Bond Certificate', icon: '🔒' },
    { id: 'contract', label: 'Contract Template', icon: '📝' },
    { id: 'certification', label: 'Certification', icon: '🏆' },
    { id: 'other', label: 'Other Document', icon: '📄' }
  ];

  // Available menu items
  const availableMenuItems = [
    { id: 'home', label: 'Today', icon: '🌅' },
    { id: 'dispatch', label: 'Dispatch', icon: '📡' },
    { id: 'field-view', label: 'My Jobs', icon: '🔧' },
    { id: 'customers', label: 'Customers & Leads', icon: '👥' },
    { id: 'work-orders', label: 'Work Orders', icon: '📋' },
    { id: 'schedule', label: 'Schedule', icon: '📅' },
    { id: 'products-pricing', label: 'Products & Pricing', icon: '💰' },
    { id: 'vendors', label: 'Vendors', icon: '🏢' },
    { id: 'inventory', label: 'Inventory', icon: '📦' },
    { id: 'reorder-list', label: 'Reorder List', icon: '🛒' },
    { id: 'inventory-reports', label: 'Inventory Reports', icon: '📊' },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: '📝' },
    { id: 'truck-loadout', label: 'Truck Loadout', icon: '🚛' },
    { id: 'job-templates', label: 'Job Templates', icon: '📄' },
    { id: 'vehicles', label: 'Vehicles', icon: '🚐' },
    { id: 'locations', label: 'Locations', icon: '🏭' },
    { id: 'local-resources', label: 'Local Resources', icon: '📍' },
    { id: 'sales-tool', label: 'Sales Tool', icon: '📸' },
    { id: 'financial', label: 'Reporting & Finance', icon: '📊' },
    { id: 'money', label: 'Money', icon: '💲' },
{ id: 'team', label: 'Team', icon: '👤' },
    { id: 'referral', label: 'Earn With WorkVan', icon: '🎁' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];


  // Handle initial section navigation
  useEffect(() => {
    if (initialSection === 'payment') {
      setActiveTab('payments');
    } else if (initialSection === 'referral') {
      setActiveTab('referral');
    }
  }, [initialSection]);

  useEffect(() => {
    if (companyId) {
      loadCompanySettings();
    }
  }, [companyId]);

  // Load documents when switching to documents tab
  useEffect(() => {
    if (activeTab === 'documents' && companyId) {
      loadDocuments();
    }
  }, [activeTab, companyId]);

  // Load message templates when switching to communications tab
  useEffect(() => {
    if (activeTab === 'communications' && companyId) {
      loadMessageTemplates();
    }
  }, [activeTab, companyId]);

  // Load referral earnings when switching to referral tab
  useEffect(() => {
    if (activeTab === 'referral' && companyId) {
      loadReferralData();
    }
  }, [activeTab, companyId]);

  // Refresh offline cache info when viewing business tab
  useEffect(() => {
    if (activeTab === 'business' && offlineEnabled && companyId) {
      setCacheSize(getCacheSize());
      setCacheSummary(getCacheSummary(companyId));
    }
  }, [activeTab, offlineEnabled, companyId]);

  const handleToggleOfflineMode = async (enabled) => {
    setOfflineModeEnabled(enabled);
    setOfflineEnabled(enabled);
    if (enabled && companyId) {
      setCacheWarming(true);
      setCacheProgress({ current: 0, total: 9, table: '' });
      try {
        await warmCache(companyId, supabaseData, (current, total, table) => {
          setCacheProgress({ current, total, table });
        });
        setCacheSize(getCacheSize());
        setCacheSummary(getCacheSummary(companyId));
        showSuccess('Data cached for offline use');
      } catch (e) {
        showError('Failed to cache some data: ' + e.message);
      } finally {
        setCacheWarming(false);
      }
    }
  };

  const handleRefreshCache = async () => {
    if (!companyId) return;
    setCacheWarming(true);
    setCacheProgress({ current: 0, total: 9, table: '' });
    try {
      await warmCache(companyId, supabaseData, (current, total, table) => {
        setCacheProgress({ current, total, table });
      });
      setCacheSize(getCacheSize());
      setCacheSummary(getCacheSummary(companyId));
      showSuccess('Cache refreshed');
    } catch (e) {
      showError('Failed to refresh cache');
    } finally {
      setCacheWarming(false);
    }
  };

  const handleClearCache = () => {
    clearCache();
    setOfflineModeEnabled(false);
    setOfflineEnabled(false);
    setCacheSummary(null);
    setCacheSize('0');
    showSuccess('Offline cache cleared');
  };

  const loadReferralData = async () => {
    setLoadingReferrals(true);
    try {
      const [creditsResult, companiesResult] = await Promise.all([
        supabaseData
          .from('referral_credits')
          .select('*')
          .eq('referrer_company_id', companyId)
          .order('created_at', { ascending: false }),
        supabaseData
          .from('companies')
          .select('id, name, created_at')
          .eq('referred_by_company_id', companyId)
      ]);
      setReferralCredits(creditsResult.data || []);
      setReferredCompanies(companiesResult.data || []);
    } catch (err) {
      // Silently fail - referral data is non-critical
    } finally {
      setLoadingReferrals(false);
    }
  };

  // Load recovery contacts when on account tab
  useEffect(() => {
    if (activeTab === 'account' && user?.id) {
      loadRecoveryContacts();
    }
  }, [activeTab, user?.id]);

  // Initialize editing name from userProfile or auth metadata as fallback
  useEffect(() => {
    const name = userProfile?.full_name || user?.user_metadata?.full_name || '';
    if (name) {
      setEditingName(name);
    }
  }, [userProfile?.full_name, user?.user_metadata?.full_name]);

  const saveUserName = async () => {
    if (!user?.id || !editingName.trim()) return;
    setSavingName(true);
    try {
      // Update user metadata in Supabase Auth
      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: editingName.trim() }
      });
      if (authError) throw authError;

      // Also update user_profiles table if it exists
      await supabaseData
        .from('user_profiles')
        .upsert({
          id: user.id,
          full_name: editingName.trim(),
          updated_at: new Date().toISOString()
        }, { onConflict: 'id' });

      showSuccess('Name updated successfully!');
      if (onProfileUpdate) {
        onProfileUpdate({ ...userProfile, full_name: editingName.trim() });
      }
    } catch (error) {

      showError('Failed to save name');
    } finally {
      setSavingName(false);
    }
  };

  const loadRecoveryContacts = async () => {
    if (!user?.id) return;
    setLoadingRecovery(true);
    try {
      const { data, error } = await supabaseData
        .from('recovery_contacts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRecoveryContacts(data || []);
    } catch (err) {

    } finally {
      setLoadingRecovery(false);
    }
  };

  const addRecoveryContact = async (type, value) => {
    if (!user?.id || !value) return;

    // Validation
    if (type === 'phone') {
      const phoneDigits = value.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 15) {
        showError('Please enter a valid phone number (10-15 digits)');
        return;
      }
    } else if (type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        showError('Please enter a valid email address');
        return;
      }
    }

    setAddingRecoveryContact(true);
    try {
      // Generate verification code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes

      const { data, error } = await supabaseData
        .from('recovery_contacts')
        .insert({
          user_id: user.id,
          contact_type: type,
          contact_value: value,
          verification_code: code,
          verification_expires_at: expiresAt.toISOString(),
          is_verified: false
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          showError('This contact is already added to your account.');
        } else {
          throw error;
        }
        return;
      }

      // Send verification code (would call an API endpoint in production)
      if (type === 'phone') {
        // In production, send SMS via API
        showAlert(`A verification code has been sent to ${formatPhoneDisplay(value)}. Code: ${code}`, { title: 'Verification Sent' });
      } else {
        // In production, send email via API
        showAlert(`A verification code has been sent to ${value}. Code: ${code}`, { title: 'Verification Sent' });
      }

      setRecoveryContacts(prev => [data, ...prev]);
      setNewRecoveryPhone('');
      setNewRecoveryEmail('');
      setVerifyingContact(data.id);
    } catch (err) {

      showError('Failed to add recovery contact. Please try again.');
    } finally {
      setAddingRecoveryContact(false);
    }
  };

  const verifyRecoveryContact = async (contactId) => {
    if (!verificationCode) return;
    try {
      const contact = recoveryContacts.find(c => c.id === contactId);
      if (!contact) return;

      if (new Date(contact.verification_expires_at) < new Date()) {
        showError('Verification code has expired. Please remove and re-add this contact.');
        return;
      }

      if (contact.verification_code !== verificationCode) {
        showError('Invalid verification code. Please try again.');
        return;
      }

      const { error } = await supabaseData
        .from('recovery_contacts')
        .update({ is_verified: true, verification_code: null })
        .eq('id', contactId);

      if (error) throw error;

      setRecoveryContacts(prev =>
        prev.map(c => c.id === contactId ? { ...c, is_verified: true } : c)
      );
      setVerifyingContact(null);
      setVerificationCode('');
      showSuccess('Recovery contact verified successfully!');
    } catch (err) {

      showError('Failed to verify contact. Please try again.');
    }
  };

  const removeRecoveryContact = async (contactId) => {
    const confirmed = await showConfirm('Are you sure you want to remove this recovery contact?', {
      title: 'Remove Recovery Contact',
      confirmText: 'Remove',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('recovery_contacts')
        .delete()
        .eq('id', contactId);

      if (error) throw error;
      setRecoveryContacts(prev => prev.filter(c => c.id !== contactId));
      showSuccess('Recovery contact removed.');
    } catch (err) {

      showError('Failed to remove recovery contact.');
    }
  };

  const generateRecoveryCodes = async () => {
    setGeneratingCodes(true);
    try {
      // Generate 10 recovery codes
      const codes = [];
      for (let i = 0; i < 10; i++) {
        const code = `${Math.random().toString(36).substring(2, 6)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
        codes.push(code);
      }

      // In production, hash these before storing
      // For now, store them directly (would use bcrypt in real implementation)
      const { error } = await supabaseData
        .from('recovery_codes')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      const codeRecords = codes.map(code => ({
        user_id: user.id,
        code_hash: code, // In production: await bcrypt.hash(code, 10)
        is_used: false
      }));

      const { error: insertError } = await supabaseData
        .from('recovery_codes')
        .insert(codeRecords);

      if (insertError) throw insertError;

      setRecoveryCodes(codes);
      setShowRecoveryCodes(true);
      showSuccess('New recovery codes generated. Save them in a secure place!');
    } catch (err) {

      showError('Failed to generate recovery codes.');
    } finally {
      setGeneratingCodes(false);
    }
  };

  const formatPhoneDisplay = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const loadCompanySettings = async () => {
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      // Store company info for AI
      setCompanyInfo({
        name: data?.name || data?.quote_company_name || '',
        business_type: data?.business_type || 'other',
        isRecurring: data?.recurring_billing_enabled || false,
        state: extractStateFromAddress(data?.quote_company_address || '')
      });

      setCompanySettings({
        default_view: data?.default_view ?? 'home',
        menu_order: data?.menu_order ?? null,
        logo_url: data?.logo_url ?? null,
        sales_tax_mode: data?.sales_tax_mode ?? 'optional',
        recurring_enabled: data?.recurring_enabled ?? false,
        advanced_quoting_enabled: data?.advanced_quoting_enabled ?? false,
        quote_company_name: data?.quote_company_name ?? '',
        quote_company_address: data?.quote_company_address ?? '',
        quote_company_phone: data?.quote_company_phone ?? '',
        quote_company_email: data?.quote_company_email ?? '',
        quote_custom_terms: data?.quote_custom_terms ?? '',
        quote_custom_footer: data?.quote_custom_footer ?? '',
        quote_show_logo: data?.quote_show_logo ?? true,
        quote_terms_mode: data?.quote_terms_mode ?? 'optional_on',
        sku_code_visible: data?.sku_code_visible ?? false,  // Hidden by default for simplicity
        completion_signature_mode: data?.completion_signature_mode ?? 'optional',
        // Smart Assistant
        smart_assistant_enabled: data?.smart_assistant_enabled ?? true,
        // Calendar settings
        calendar_slot_minutes: data?.calendar_slot_minutes ?? 60,
        // Branding colors
        brand_primary_color: data?.brand_primary_color ?? '#4ecca3',
        brand_secondary_color: data?.brand_secondary_color ?? '#F05A28',
        brand_accent_color: data?.brand_accent_color ?? '#3498db',
        brand_text_color: data?.brand_text_color ?? '#333333',
        // Store recurring_billing_enabled for tech permissions
        recurring_billing_enabled: data?.recurring_billing_enabled ?? false,
        // Quote fee settings
        quote_fee_enabled: data?.quote_fee_enabled ?? false,
        default_quote_fee_amount: data?.default_quote_fee_amount ?? 75.00,
        // Auto-email receipts
        auto_email_receipts: data?.auto_email_receipts ?? true,
        // Field worker title
        field_worker_title: data?.field_worker_title ?? 'Technician',
        // Auto-bill recurring
        auto_bill_recurring: data?.auto_bill_recurring ?? false,
        auto_bill_receipt_mode: data?.auto_bill_receipt_mode ?? 'always',
        // Invoice template settings
        invoice_payment_terms: data?.invoice_payment_terms ?? 'Net 30',
        invoice_footer_text: data?.invoice_footer_text ?? '',
        invoice_notes: data?.invoice_notes ?? '',
        // Customer auto-archive
        auto_archive_enabled: data?.auto_archive_enabled ?? false,
        auto_archive_inactive_days: data?.auto_archive_inactive_days ?? 365,
        auto_archive_lost_leads: data?.auto_archive_lost_leads ?? false,
        auto_archive_lost_lead_days: data?.auto_archive_lost_lead_days ?? 90,
        // Inventory settings
        inventory_enabled: data?.inventory_enabled ?? false,
        inventory_auto_deduct: data?.inventory_auto_deduct ?? true,
        inventory_low_stock_alerts: data?.inventory_low_stock_alerts ?? true,
        inventory_mode: data?.inventory_mode ?? 'simple',
        inventory_alert_email_digest: data?.inventory_alert_email_digest ?? 'none',
        inventory_digest_emails: data?.inventory_digest_emails ?? [],
        inventory_monthly_budget: data?.inventory_monthly_budget ?? 0,
        inventory_sms_critical_alerts: data?.inventory_sms_critical_alerts ?? false,
        inventory_default_warning_threshold: data?.inventory_default_warning_threshold ?? 1.5,
        inventory_default_critical_threshold: data?.inventory_default_critical_threshold ?? 0.5
      });

      // Load tech permissions
      setTechPermissions({
        tech_quoting_mode: data?.tech_quoting_mode ?? 'flexible',
        tech_can_add_manual_items: data?.tech_can_add_manual_items ?? true,
        tech_can_modify_prices: data?.tech_can_modify_prices ?? true,
        tech_max_discount_percent: data?.tech_max_discount_percent ?? 100,
        tech_can_create_service_plans: data?.tech_can_create_service_plans ?? false
      });
    } catch (error) {

    }
  };

  // Helper to extract state from address
  const extractStateFromAddress = (address) => {
    if (!address) return '';
    // Try to find a 2-letter state code
    const stateMatch = address.match(/\b([A-Z]{2})\b/);
    return stateMatch ? stateMatch[1] : '';
  };

  // Generate terms with AI
  const generateTermsWithAI = async (customInstructions = '') => {
    setGeneratingTerms(true);
    setShowAIPrompt(false);
    try {
      const response = await fetch('/api/generate-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyInfo.name || companySettings.quote_company_name || 'Our Company',
          businessType: companyInfo.business_type || 'other',
          isRecurring: companyInfo.isRecurring,
          state: companyInfo.state,
          existingTerms: companySettings.quote_custom_terms || '',
          customInstructions: customInstructions
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate terms');
      }

      const { terms } = await response.json();

      // Update the terms in state
      setCompanySettings(prev => ({ ...prev, quote_custom_terms: terms }));

      // Save to database
      await saveCompanySettings({ quote_custom_terms: terms });

      showSuccess('Terms generated! Review and edit as needed.');
      setAiPromptText(''); // Clear the prompt
    } catch (error) {

      showError('Failed to generate terms: ' + error.message);
    } finally {
      setGeneratingTerms(false);
    }
  };

  // Build AI prompt from questionnaire answers
  const buildPromptFromQuestionnaire = (answers) => {
    const instructions = [];

    // Cancellation policy
    instructions.push(`Cancellation requires ${answers.cancellation_notice_hours} hours notice.`);
    if (answers.early_termination_fee && answers.early_termination_fee_amount) {
      instructions.push(`Include early termination fee: ${answers.early_termination_fee_amount}.`);
    }
    const refundLabels = { full: 'Full refunds', prorated: 'Prorated refunds', none: 'No refunds' };
    instructions.push(`Refund policy: ${refundLabels[answers.refund_policy]} for prepaid services.`);

    // Warranties
    if (answers.warranty_period_months > 0) {
      const years = answers.warranty_period_months >= 12 ? Math.floor(answers.warranty_period_months / 12) : 0;
      const months = answers.warranty_period_months % 12;
      const periodStr = years > 0 ? (months > 0 ? `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}` : `${years} year${years > 1 ? 's' : ''}`) : `${months} month${months > 1 ? 's' : ''}`;
      instructions.push(`Include ${periodStr} warranty on workmanship.`);
    } else {
      instructions.push('No warranty on workmanship.');
    }

    if (answers.satisfaction_guarantee) {
      instructions.push('Include satisfaction guarantee clause.');
    }

    const reserviceLabels = { free: 'Free re-service', discounted: 'Discounted re-service', none: 'No re-service policy' };
    instructions.push(`Re-service policy: ${reserviceLabels[answers.reservice_policy]} if customer is unsatisfied within warranty period.`);

    // Contract renewal
    if (answers.auto_renewal) {
      instructions.push('Include auto-renewal clause with advance notice requirement to cancel.');
    }

    // Add custom flavor if provided
    if (answers.custom_flavor && answers.custom_flavor.trim()) {
      instructions.push(`Additional requirements: ${answers.custom_flavor.trim()}`);
    }

    return instructions.join(' ');
  };

  // Handle questionnaire completion
  const handleQuestionnaireComplete = () => {
    const builtPrompt = buildPromptFromQuestionnaire(termsQuestionnaireAnswers);
    setShowTermsQuestionnaire(false);
    generateTermsWithAI(builtPrompt);
  };

  // Load company documents
  const loadDocuments = async () => {
    if (!companyId) return;
    setDocumentsLoading(true);
    try {
      const { data, error } = await supabaseData
        .from('company_documents')
        .select('*')
        .eq('company_id', companyId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {

    } finally {
      setDocumentsLoading(false);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('Document must be less than 10MB');
      return;
    }

    // Validate file types
    const validTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!validTypes.includes(file.type)) {
      showError('Please upload a PDF, image, or Word document');
      return;
    }

    setUploadingDoc(true);
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}/${selectedDocType}-${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Save document record
      const { error: dbError } = await supabaseData
        .from('company_documents')
        .insert({
          company_id: companyId,
          document_type: selectedDocType,
          file_name: file.name,
          file_path: fileName,
          file_size: file.size,
          mime_type: file.type,
          description: docDescription || null,
          expiration_date: docExpirationDate || null,
          uploaded_by: user.id
        });

      if (dbError) throw dbError;

      showSuccess('Document uploaded successfully');
      setDocDescription('');
      setDocExpirationDate('');
      loadDocuments();
    } catch (error) {

      showError('Failed to upload document: ' + error.message);
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  // Delete document
  const handleDeleteDocument = async (doc) => {
    const confirmed = await showConfirm(
      `Delete "${doc.file_name}"?`,
      'This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('company-documents')
        .remove([doc.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabaseData
        .from('company_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      showSuccess('Document deleted');
      loadDocuments();
    } catch (error) {

      showError('Failed to delete document');
    }
  };

  // View/download document
  const handleViewDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('company-documents')
        .createSignedUrl(doc.file_path, 300); // 5 minute expiry

      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (error) {

      showError('Failed to open document');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if document is expiring soon (within 30 days)
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const expiry = new Date(date);
    const now = new Date();
    const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Please upload a valid image file (JPEG, PNG, GIF, or WebP). SVG files are not supported for cropping.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('Logo must be less than 2MB');
      return;
    }

    // Read file and open crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImageSrc(e.target.result);
      setCropScale(1);
      setCropPosition({ x: 0, y: 0 });
      setShowLogoCropModal(true);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle zoom in/out
  const handleZoom = (direction) => {
    setCropScale(prev => {
      const newScale = direction === 'in' ? prev + 0.1 : prev - 0.1;
      return Math.max(0.1, Math.min(3, newScale)); // Limit between 10% and 300%
    });
  };

  // Handle mouse down for dragging
  const handleCropMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - cropPosition.x,
      y: e.clientY - cropPosition.y
    });
  };

  // Handle mouse move for dragging
  const handleCropMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setCropPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  // Handle mouse up to stop dragging
  const handleCropMouseUp = () => {
    setIsDragging(false);
  };

  // Handle touch events for mobile
  const handleCropTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - cropPosition.x,
      y: touch.clientY - cropPosition.y
    });
  };

  const handleCropTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setCropPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const handleCropTouchEnd = () => {
    setIsDragging(false);
  };

  // Reset crop to center
  const resetCrop = () => {
    setCropScale(1);
    setCropPosition({ x: 0, y: 0 });
  };

  // Crop and upload the logo
  const cropAndUploadLogo = async () => {
    if (!cropImageRef.current || !cropCanvasRef.current) return;
    
    setLogoUploading(true);
    try {
      const canvas = cropCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = cropImageRef.current;
      
      // Set canvas size to 400x400 (good quality for logo)
      const outputSize = 400;
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      // Calculate dimensions to fill the canvas while maintaining aspect ratio
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const canvasAspect = 1; // Square output
      
      let drawWidth, drawHeight, sourceX, sourceY;
      
      // Calculate scaled dimensions
      if (imgAspect > canvasAspect) {
        // Image is wider - fit to height
        drawHeight = outputSize / cropScale;
        drawWidth = drawHeight * imgAspect;
      } else {
        // Image is taller - fit to width
        drawWidth = outputSize / cropScale;
        drawHeight = drawWidth / imgAspect;
      }
      
      // Apply pan offset
      const offsetX = cropPosition.x * (drawWidth / outputSize);
      const offsetY = cropPosition.y * (drawHeight / outputSize);
      
      // Center the image and apply pan
      sourceX = (img.naturalWidth - (img.naturalWidth / cropScale)) / 2 - offsetX;
      sourceY = (img.naturalHeight - (img.naturalHeight / cropScale)) / 2 - offsetY;
      
      // Ensure we don't show empty space
      const sourceWidth = img.naturalWidth / cropScale;
      const sourceHeight = img.naturalHeight / cropScale;
      
      // Fill canvas with white background (for transparent images)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outputSize, outputSize);
      
      // Draw the cropped image
      ctx.drawImage(
        img,
        Math.max(0, sourceX),
        Math.max(0, sourceY),
        Math.min(img.naturalWidth, sourceWidth),
        Math.min(img.naturalHeight, sourceHeight),
        0,
        0,
        outputSize,
        outputSize
      );
      
      // Convert to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png', 0.9);
      });
      
      // Create unique filename
      const fileName = `${companyId}-logo-${Date.now()}.png`;
      const filePath = `logos/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/png'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-assets')
        .getPublicUrl(filePath);

      // Save URL to company record
      await saveCompanySettings({ logo_url: publicUrl });

      // Clear the company cache so the logo updates in the header
      localStorage.removeItem(`company_${user.id}`);

      showSuccess('Logo uploaded successfully! Refresh to see it in the header.');
      
      // Close modal and reset
      setShowLogoCropModal(false);
      setCropImageSrc(null);
      setCropScale(1);
      setCropPosition({ x: 0, y: 0 });
    } catch (error) {
      showError('Error uploading logo: ' + error.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    const confirmed = await showConfirm('Remove company logo?', {
      title: 'Remove Logo',
      confirmText: 'Remove',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    try {
      await saveCompanySettings({ logo_url: null });
      // Clear cache
      localStorage.removeItem(`company_${user.id}`);
    } catch (error) {

    }
  };

  const saveCompanySettings = async (updates, options = {}) => {
    const { silent = false } = options;
    try {
      setSaving(true);
      if (!silent) {
        if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
        setSaveStatus('saving');
      }

      const { error } = await supabaseData
        .from('companies')
        .update(updates)
        .eq('id', companyId);

      if (error) throw error;

      setCompanySettings(prev => ({ ...prev, ...updates }));

      // Notify parent component (App.js) to update its companyData state
      // This ensures the menu and other components reflect changes immediately
      if (onCompanyUpdate) {
        onCompanyUpdate(updates);
      }

      if (!silent) {
        setSaveStatus('saved');
        saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      }
    } catch (error) {
      setSaveStatus('error');
      saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 4000);
      showError('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Save Tech Permission
  const saveTechPermission = async (field, value) => {
    if (!companyId) return;
    setSavingPermissions(true);

    try {
      const { error } = await supabaseData
        .from('companies')
        .update({ [field]: value })
        .eq('id', companyId);

      if (error) throw error;

      setTechPermissions(prev => ({ ...prev, [field]: value }));
      showSuccess('Permission saved!');
    } catch (error) {
      showError('Error saving permission: ' + error.message);
    } finally {
      setSavingPermissions(false);
    }
  };

  // Message Template Functions
  const loadMessageTemplates = async () => {
    if (!companyId) return;
    setLoadingTemplates(true);
    try {
      const { data, error } = await supabaseData
        .from('message_templates')
        .select('*')
        .eq('company_id', companyId)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setMessageTemplates(data || []);
    } catch (error) {

      showError('Failed to load message templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        message: template.message,
        channel: template.channel,
        category: template.category || ''
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        message: '',
        channel: 'sms',
        category: ''
      });
    }
    setShowTemplateModal(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.message.trim()) {
      showError('Please enter a name and message');
      return;
    }

    setSavingTemplate(true);
    console.log('[Settings] saveTemplate starting...');

    // Timeout wrapper to prevent hanging
    const withTimeout = (promise, ms = 15000) => {
      return Promise.race([
        promise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out')), ms)
        )
      ]);
    };

    try {
      const templateData = {
        company_id: companyId,
        name: templateForm.name.trim(),
        message: templateForm.message.trim(),
        channel: templateForm.channel,
        category: templateForm.category || null,
        updated_at: new Date().toISOString()
      };

      console.log('[Settings] Template data:', templateData);

      if (editingTemplate) {
        console.log('[Settings] Updating template...');
        const { error } = await withTimeout(supabaseData
          .from('message_templates')
          .update(templateData)
          .eq('id', editingTemplate.id));
        console.log('[Settings] Update result:', { error });
        if (error) throw error;
        showSuccess('Template updated!');
      } else {
        console.log('[Settings] Inserting new template...');
        const { error } = await withTimeout(supabaseData
          .from('message_templates')
          .insert([templateData]));
        console.log('[Settings] Insert result:', { error });
        if (error) throw error;
        showSuccess('Template created!');
      }

      setShowTemplateModal(false);
      loadMessageTemplates();
    } catch (error) {
      console.error('[Settings] saveTemplate error:', error);
      showError('Failed to save template: ' + (error.message || 'Unknown error'));
    } finally {
      console.log('[Settings] saveTemplate finished');
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (template) => {
    const confirmed = await showConfirm(
      'Delete Template',
      `Are you sure you want to delete "${template.name}"?`
    );
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('message_templates')
        .delete()
        .eq('id', template.id);
      if (error) throw error;
      showSuccess('Template deleted');
      loadMessageTemplates();
    } catch (error) {

      showError('Failed to delete template');
    }
  };

  const insertVariable = (variable) => {
    setTemplateForm(prev => ({
      ...prev,
      message: prev.message + variable
    }));
  };

  const filteredTemplates = messageTemplates.filter(t =>
    templateFilter === 'all' || t.channel === templateFilter
  );

  const getCurrentMenuOrder = () => {
    if (companySettings.menu_order && Array.isArray(companySettings.menu_order)) {
      // Filter out any legacy IDs that are no longer valid (e.g., 'invoicing')
      const validIds = availableMenuItems.map(item => item.id);
      return companySettings.menu_order.filter(id => validIds.includes(id));
    }
    return availableMenuItems.map(item => item.id);
  };

  const moveMenuItem = (index, direction) => {
    const currentOrder = getCurrentMenuOrder();
    const newOrder = [...currentOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newOrder.length) return;

    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    saveCompanySettings({ menu_order: newOrder }, { silent: true });
  };

  const resetMenuOrder = () => {
    const defaultOrder = availableMenuItems.map(item => item.id);
    saveCompanySettings({ menu_order: defaultOrder }, { silent: true });
  };

  const removeMenuItem = (itemId) => {
    // Don't allow removing settings - users need access to restore items
    if (itemId === 'settings') return;
    const currentOrder = getCurrentMenuOrder();
    const newOrder = currentOrder.filter(id => id !== itemId);
    saveCompanySettings({ menu_order: newOrder }, { silent: true });
  };

  const restoreMenuItem = (itemId) => {
    const currentOrder = getCurrentMenuOrder();
    // Add to end of list
    const newOrder = [...currentOrder, itemId];
    saveCompanySettings({ menu_order: newOrder }, { silent: true });
  };

  const getHiddenMenuItems = () => {
    const currentOrder = getCurrentMenuOrder();
    return availableMenuItems.filter(item => !currentOrder.includes(item.id));
  };

  // Export all company data (GDPR/CCPA compliance)
  const handleExportAllData = async () => {
    setExporting(true);
    try {
      // Fetch all data types
      const [customersRes, jobsRes, quotesRes, invoicesRes, commsRes] = await Promise.all([
        supabaseData.from('customers').select('*').eq('company_id', companyId),
        supabaseData.from('jobs').select('*, customers(name, email)').eq('company_id', companyId),
        supabaseData.from('quotes').select('*, customers(name, email)').eq('company_id', companyId),
        supabaseData.from('invoices').select('*, customers(name, email)').eq('company_id', companyId),
        supabaseData.from('communications').select('*, customers(name)').eq('company_id', companyId)
      ]);

      const customers = customersRes.data || [];
      const jobs = jobsRes.data || [];
      const quotes = quotesRes.data || [];
      const invoices = invoicesRes.data || [];
      const communications = commsRes.data || [];

      // Helper to convert to CSV
      const toCSV = (data, columns) => {
        if (!data.length) return columns.join(',') + '\n';
        const header = columns.join(',');
        const rows = data.map(row =>
          columns.map(col => {
            let val = row[col];
            if (val === null || val === undefined) return '""';
            if (typeof val === 'object') val = JSON.stringify(val);
            return `"${String(val).replace(/"/g, '""')}"`;
          }).join(',')
        );
        return [header, ...rows].join('\n');
      };

      // Create CSVs
      const customersCSV = toCSV(customers, ['id', 'name', 'email', 'phone', 'address', 'city', 'state', 'zip', 'notes', 'created_at']);
      const jobsCSV = toCSV(jobs.map(j => ({
        ...j,
        customer_name: j.customers?.name || '',
        customer_email: j.customers?.email || ''
      })), ['id', 'job_number', 'customer_name', 'customer_email', 'type', 'status', 'scheduled_date', 'completed_date', 'total_amount', 'notes', 'created_at']);
      const quotesCSV = toCSV(quotes.map(q => ({
        ...q,
        customer_name: q.customers?.name || ''
      })), ['id', 'quote_number', 'customer_name', 'status', 'total', 'valid_until', 'created_at']);
      const invoicesCSV = toCSV(invoices.map(i => ({
        ...i,
        customer_name: i.customers?.name || ''
      })), ['id', 'invoice_number', 'customer_name', 'total', 'status', 'due_date', 'paid_date', 'created_at']);
      const communicationsCSV = toCSV(communications.map(c => ({
        ...c,
        customer_name: c.customers?.name || ''
      })), ['id', 'customer_name', 'type', 'direction', 'message', 'status', 'sent_at']);

      // Company info JSON
      const companyInfo = {
        exported_at: new Date().toISOString(),
        company_id: companyId,
        export_includes: {
          customers: customers.length,
          jobs: jobs.length,
          quotes: quotes.length,
          invoices: invoices.length,
          communications: communications.length
        }
      };

      // Create README
      const readme = `Work Van Data Export
====================
Exported: ${new Date().toLocaleString()}

This export contains:
- customers.csv (${customers.length} records)
- jobs.csv (${jobs.length} records)
- quotes.csv (${quotes.length} records)
- invoices.csv (${invoices.length} records)
- communications.csv (${communications.length} records)
- company-info.json (export metadata)

All files are in CSV format (comma-separated values) and can be opened with Excel, Google Sheets, or any spreadsheet application.
`;

      // Create ZIP using JSZip
      if (JSZip) {
        const zip = new JSZip();
        zip.file('customers.csv', customersCSV);
        zip.file('jobs.csv', jobsCSV);
        zip.file('quotes.csv', quotesCSV);
        zip.file('invoices.csv', invoicesCSV);
        zip.file('communications.csv', communicationsCSV);
        zip.file('company-info.json', JSON.stringify(companyInfo, null, 2));
        zip.file('README.txt', readme);

        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workvan-export-${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // Fallback: download customers CSV only with all data summary
        const allData = `${readme}\n\n=== CUSTOMERS ===\n${customersCSV}\n\n=== JOBS ===\n${jobsCSV}\n\n=== QUOTES ===\n${quotesCSV}\n\n=== INVOICES ===\n${invoicesCSV}\n\n=== COMMUNICATIONS ===\n${communicationsCSV}`;
        const blob = new Blob([allData], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workvan-export-${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      }

      showSuccess('Data export complete!');
    } catch (error) {

      showError('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      showError('Please type DELETE MY ACCOUNT exactly to confirm');
      return;
    }

    setDeleting(true);
    try {
      // Use window.location.origin to ensure correct API path
      const apiUrl = `${window.location.origin}/api/account-delete`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user?.id,
          confirmationText: deleteConfirmText
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response isn't JSON, create a generic error
        data = { error: 'Unexpected response from server. Please try again.' };
      }

      if (!response.ok) {
        // Backend returns `code` (older path also wrote `reason`) — accept either
        const errorCode = data.code || data.reason;
        if (errorCode === 'COMPANY_HAS_MEMBERS') {
          // Specific actionable path: close the delete modal and offer to
          // jump to the Team tab so they can remove the blocking members.
          setShowDeleteConfirm(false);
          const ok = await showConfirm(
            "Can't delete yet",
            `${data.message || 'Your company still has other team members.'}\n\nWant to go to the Team tab now to remove them?`
          );
          if (ok) setActiveTab('team');
        } else if (errorCode === 'STRIPE_CANCEL_FAILED') {
          showError(data.error || 'Could not cancel your subscription with Stripe. Please contact support so we can finish this without losing track of your billing.');
        } else if (data.error?.includes('DELETE MY ACCOUNT')) {
          showError('Please type DELETE MY ACCOUNT exactly to confirm');
        } else {
          showError(data.error || data.message || `Failed to delete account (${response.status})`);
        }
        return;
      }

      showSuccess('Account deleted successfully. Goodbye!');

      // Clear all local storage data
      localStorage.clear();

      // Short delay to show success message, then force logout and redirect
      setTimeout(async () => {
        try {
          await supabase.auth.signOut();
        } catch (e) {
          // User may already be gone from auth - that's fine
        }
        // Force redirect to landing page regardless of signOut result
        window.location.href = '/';
      }, 1500);
    } catch (error) {
      console.error('Delete account error:', error);
      showError('Failed to delete account: ' + (error.message || 'Network error. Please check your connection and try again.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', overflowX: 'hidden' }}>
      {/* Auto-save indicator — fixed, appears briefly after each change */}
      {saveStatus !== 'idle' && (
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '0.5rem 1rem',
            borderRadius: '999px',
            background: saveStatus === 'saved' ? '#10b98120' : saveStatus === 'error' ? '#ef444420' : colors.bgCard,
            border: `1px solid ${saveStatus === 'saved' ? '#10b981' : saveStatus === 'error' ? '#ef4444' : colors.border}`,
            color: saveStatus === 'saved' ? '#10b981' : saveStatus === 'error' ? '#ef4444' : colors.textMuted,
            fontSize: '0.85rem',
            fontWeight: 600,
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'opacity 0.2s',
          }}
        >
          {saveStatus === 'saving' && (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
              Saving...
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              Changes saved
            </>
          )}
          {saveStatus === 'error' && (
            <>Save failed — try again</>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
          ⚙️ Settings
        </h1>
        <p style={{ margin: 0, color: colors.textMuted }}>
          Manage your account, team, and preferences
        </p>
      </div>

      {/* Tabs - horizontally scrollable on mobile */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: isMobile ? '0.75rem' : '1rem 1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`,
        boxSizing: 'border-box',
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          overflowX: isMobile ? 'auto' : 'visible',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: '0.5rem',
          marginBottom: '-0.5rem'
        }} className="tabs-scroll">
          <button
            onClick={() => setActiveTab('account')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'account' ? colors.accent : colors.bgInput,
              color: activeTab === 'account' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <User size={18} />
            Account
          </button>
          <button
            onClick={() => setActiveTab('team')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'team' ? colors.accent : colors.bgInput,
              color: activeTab === 'team' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Users size={18} />
            Team
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'notifications' ? colors.accent : colors.bgInput,
              color: activeTab === 'notifications' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Bell size={18} />
            Alerts
          </button>
          <button
            onClick={() => setActiveTab('business')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'business' ? colors.accent : colors.bgInput,
              color: activeTab === 'business' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Briefcase size={18} />
            Business
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'documents' ? colors.accent : colors.bgInput,
              color: activeTab === 'documents' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <FolderOpen size={18} />
            Documents
          </button>
          <button
            onClick={() => setActiveTab('quotes')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'quotes' ? colors.accent : colors.bgInput,
              color: activeTab === 'quotes' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <FileText size={18} />
            Quotes
          </button>
          <button
            onClick={() => setActiveTab('communications')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'communications' ? colors.accent : colors.bgInput,
              color: activeTab === 'communications' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <MessageSquare size={18} />
            Communications
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'payments' ? colors.accent : colors.bgInput,
              color: activeTab === 'payments' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <CreditCard size={18} />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'subscription' ? colors.accent : colors.bgInput,
              color: activeTab === 'subscription' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Crown size={18} />
            Plan
          </button>
          <button
            onClick={() => setActiveTab('appearance')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'appearance' ? colors.accent : colors.bgInput,
              color: activeTab === 'appearance' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Palette size={18} />
            Appearance
          </button>
          <button
            onClick={() => setActiveTab('referral')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'referral' ? colors.accent : colors.bgInput,
              color: activeTab === 'referral' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🤝</span>
            Earn With WorkVan
          </button>
          <button
            onClick={() => setActiveTab('chemicals')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'chemicals' ? colors.accent : colors.bgInput,
              color: activeTab === 'chemicals' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Beaker size={18} />
            Chemicals
          </button>
          <button
            onClick={() => setActiveTab('quotevisits')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'quotevisits' ? colors.accent : colors.bgInput,
              color: activeTab === 'quotevisits' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <DollarSign size={18} />
            Quote Visits
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'inventory' ? colors.accent : colors.bgInput,
              color: activeTab === 'inventory' ? '#fff' : colors.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
              minHeight: '44px',
              whiteSpace: 'nowrap'
            }}
          >
            <Package size={18} />
            Inventory
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'account' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1.5rem 0', color: colors.text }}>
            Account Information
          </h2>
          <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '600px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                Email
              </label>
              <div style={{
                padding: '0.75rem',
                background: colors.bgInput,
                borderRadius: '8px',
                color: colors.text
              }}>
                {user?.email || 'Not available'}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                Full Name
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="Enter your name"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
                {editingName !== (userProfile?.full_name || '') && (
                  <button
                    onClick={saveUserName}
                    disabled={savingName}
                    style={{
                      padding: '0.75rem 1rem',
                      background: colors.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: savingName ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {savingName ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                Role
              </label>
              <div style={{
                padding: '0.75rem',
                background: colors.bgInput,
                borderRadius: '8px',
                color: colors.text,
                textTransform: 'capitalize'
              }}>
                {userRole || 'Member'}
              </div>
            </div>
          </div>

          {/* Account Recovery Options */}
          <div style={{
            marginTop: '2rem',
            padding: isMobile ? '1rem' : '1.5rem',
            background: colors.bgInput,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <Shield size={24} color={colors.accent} />
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Account Recovery Options
              </h3>
            </div>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Add backup contact methods to recover your account if you lose access to your primary email.
              This is especially important since your business depends on this account.
            </p>

            {/* Existing Recovery Contacts */}
            {loadingRecovery ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textMuted }}>
                <Loader size={16} className="spin" /> Loading recovery options...
              </div>
            ) : (
              <>
                {recoveryContacts.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                      Your Recovery Contacts
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {recoveryContacts.map(contact => (
                        <div key={contact.id} style={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'stretch' : 'center',
                          justifyContent: 'space-between',
                          padding: isMobile ? '0.75rem' : '0.75rem 1rem',
                          background: colors.bgCard,
                          borderRadius: '8px',
                          border: `1px solid ${colors.border}`,
                          flexWrap: 'wrap',
                          gap: '0.5rem',
                          minWidth: 0,
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          overflow: 'hidden'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flexWrap: 'wrap', maxWidth: '100%' }}>
                            {contact.contact_type === 'phone' ? (
                              <Phone size={18} color={colors.textMuted} style={{ flexShrink: 0 }} />
                            ) : (
                              <Mail size={18} color={colors.textMuted} style={{ flexShrink: 0 }} />
                            )}
                            <span style={{ color: colors.text, overflow: 'hidden', wordBreak: 'break-all', overflowWrap: 'anywhere', minWidth: 0, flex: '1 1 auto' }}>
                              {contact.contact_type === 'phone'
                                ? formatPhoneDisplay(contact.contact_value)
                                : contact.contact_value}
                            </span>
                            {contact.is_verified ? (
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.2rem 0.5rem',
                                background: `${colors.success}20`,
                                color: colors.success,
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                <Check size={12} /> Verified
                              </span>
                            ) : (
                              <span style={{
                                padding: '0.2rem 0.5rem',
                                background: `${colors.warning}20`,
                                color: colors.warning,
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                Pending
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            {!contact.is_verified && verifyingContact === contact.id && (
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <input
                                  type="text"
                                  placeholder="Enter code"
                                  value={verificationCode}
                                  onChange={(e) => setVerificationCode(e.target.value)}
                                  style={{
                                    width: isMobile ? '90px' : '100px',
                                    boxSizing: 'border-box',
                                    padding: '0.4rem 0.6rem',
                                    background: colors.bgInput,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '6px',
                                    color: colors.text,
                                    fontSize: '0.85rem'
                                  }}
                                />
                                <button
                                  onClick={() => verifyRecoveryContact(contact.id)}
                                  style={{
                                    padding: '0.4rem 0.75rem',
                                    background: colors.accent,
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#fff',
                                    fontSize: '0.85rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Verify
                                </button>
                              </div>
                            )}
                            {!contact.is_verified && verifyingContact !== contact.id && (
                              <button
                                onClick={() => setVerifyingContact(contact.id)}
                                style={{
                                  padding: '0.4rem 0.75rem',
                                  background: 'transparent',
                                  border: `1px solid ${colors.accent}`,
                                  borderRadius: '6px',
                                  color: colors.accent,
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Enter Code
                              </button>
                            )}
                            <button
                              onClick={() => removeRecoveryContact(contact.id)}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: 'transparent',
                                border: `1px solid ${colors.error}`,
                                borderRadius: '6px',
                                color: colors.error,
                                fontSize: '0.85rem',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Recovery Contact */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      Backup Phone Number
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', minWidth: 0 }}>
                      <input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={newRecoveryPhone}
                        onChange={(e) => {
                          setNewRecoveryPhone(e.target.value);
                          const digits = e.target.value.replace(/\D/g, '');
                          if (e.target.value && (digits.length < 10 || digits.length > 15)) {
                            setRecoveryPhoneError('Enter 10-15 digits');
                          } else {
                            setRecoveryPhoneError('');
                          }
                        }}
                        style={{
                          flex: '1 1 140px',
                          minWidth: 0,
                          boxSizing: 'border-box',
                          padding: '0.75rem',
                          background: colors.bgCard,
                          border: `2px solid ${recoveryPhoneError ? '#ef4444' : newRecoveryPhone ? colors.accent : colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                      <button
                        onClick={() => {
                          const digits = newRecoveryPhone.replace(/\D/g, '');
                          if (digits.length < 10 || digits.length > 15) {
                            setRecoveryPhoneError('Enter 10-15 digits');
                            return;
                          }
                          addRecoveryContact('phone', digits);
                        }}
                        disabled={!newRecoveryPhone || addingRecoveryContact || !!recoveryPhoneError}
                        style={{
                          padding: '0.75rem 1rem',
                          background: newRecoveryPhone ? colors.accent : colors.bgInput,
                          border: 'none',
                          borderRadius: '8px',
                          color: newRecoveryPhone ? '#fff' : colors.textMuted,
                          fontWeight: '600',
                          cursor: newRecoveryPhone && !addingRecoveryContact ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <Phone size={16} /> Add
                      </button>
                    </div>
                    {recoveryPhoneError && (
                      <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                        {recoveryPhoneError}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      Secondary Email
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', minWidth: 0 }}>
                      <input
                        type="email"
                        placeholder="backup@example.com"
                        value={newRecoveryEmail}
                        onChange={(e) => {
                          setNewRecoveryEmail(e.target.value);
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (e.target.value && !emailRegex.test(e.target.value)) {
                            setRecoveryEmailError('Enter valid email');
                          } else {
                            setRecoveryEmailError('');
                          }
                        }}
                        style={{
                          flex: '1 1 140px',
                          minWidth: 0,
                          boxSizing: 'border-box',
                          padding: '0.75rem',
                          background: colors.bgCard,
                          border: `2px solid ${recoveryEmailError ? '#ef4444' : newRecoveryEmail ? colors.accent : colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                      <button
                        onClick={() => {
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(newRecoveryEmail)) {
                            setRecoveryEmailError('Enter valid email');
                            return;
                          }
                          addRecoveryContact('email', newRecoveryEmail);
                        }}
                        disabled={!newRecoveryEmail || addingRecoveryContact || !!recoveryEmailError}
                        style={{
                          padding: '0.75rem 1rem',
                          background: newRecoveryEmail ? colors.accent : colors.bgInput,
                          border: 'none',
                          borderRadius: '8px',
                          color: newRecoveryEmail ? '#fff' : colors.textMuted,
                          fontWeight: '600',
                          cursor: newRecoveryEmail && !addingRecoveryContact ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <Mail size={16} /> Add
                      </button>
                    </div>
                    {recoveryEmailError && (
                      <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                        {recoveryEmailError}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Your Data - Export */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: colors.bgInput,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <Download size={24} color={colors.accent} />
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Your Data
              </h3>
            </div>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Export all your company data including customers, jobs, quotes, invoices, and communications.
              This download is yours to keep and can be imported into other systems.
            </p>
            <button
              onClick={handleExportAllData}
              disabled={exporting}
              style={{
                padding: '0.75rem 1.5rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: exporting ? 'wait' : 'pointer',
                opacity: exporting ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '44px'
              }}
            >
              {exporting ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Export All Data
                </>
              )}
            </button>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>

          {/* Reset Test Account - Only for @workvanapp.com emails */}
          {user?.email?.endsWith('@workvanapp.com') && (
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: '#fef3c7',
              borderRadius: '12px',
              border: '1px solid #f59e0b'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <RefreshCw size={24} color="#d97706" />
                <h3 style={{ margin: 0, color: '#92400e', fontSize: '1.25rem' }}>
                  Reset Test Account
                </h3>
              </div>
              <p style={{ color: '#92400e', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Wipe all data from this test account (customers, jobs, quotes, invoices, products) and reset onboarding.
                Use this to test the signup/onboarding flow again.
              </p>
              <button
                onClick={async () => {
                  const confirmed = await showConfirm(
                    'Reset Test Account?',
                    'This will delete ALL data in this account and reset onboarding. This cannot be undone. Continue?'
                  );
                  if (!confirmed) return;

                  try {
                    // Delete all data in order (respecting foreign keys)
                    const tables = [
                      'communications',
                      'quote_items',
                      'invoices',
                      'jobs',
                      'quotes',
                      'customers',
                      'skus',
                      'notifications'
                    ];

                    for (const table of tables) {
                      const { error } = await supabaseData
                        .from(table)
                        .delete()
                        .eq('company_id', companyId);

                    }

                    // Reset onboarding flag
                    await supabaseData
                      .from('companies')
                      .update({ onboarding_products_skipped: false })
                      .eq('id', companyId);

                    // Clear localStorage cache
                    const keys = Object.keys(localStorage);
                    keys.forEach(key => {
                      if (key.startsWith('company_')) {
                        localStorage.removeItem(key);
                      }
                    });

                    showSuccess('Test account reset! Refreshing...');
                    setTimeout(() => window.location.reload(), 1500);
                  } catch (error) {
                    showError('Reset failed: ' + error.message);
                  }
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f59e0b',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minHeight: '44px'
                }}
              >
                <RefreshCw size={18} />
                Reset All Data
              </button>
            </div>
          )}

          {/* Danger Zone */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: `${colors.error}10`,
            borderRadius: '12px',
            border: `1px solid ${colors.error}40`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <AlertTriangle size={24} color={colors.error} />
              <h3 style={{ margin: 0, color: colors.error, fontSize: '1.25rem' }}>
                Danger Zone
              </h3>
            </div>

            {!showDeleteConfirm ? (
              <div>
                <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={!user?.id}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'transparent',
                    border: `2px solid ${colors.error}`,
                    borderRadius: '8px',
                    color: colors.error,
                    fontWeight: 'bold',
                    cursor: user?.id ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    minHeight: '44px',
                    opacity: user?.id ? 1 : 0.5
                  }}
                >
                  <Trash2 size={18} />
                  Delete Account
                </button>
              </div>
            ) : (
              <div style={{
                padding: '1.5rem',
                background: colors.bgCard,
                borderRadius: '8px',
                border: `2px solid ${colors.error}50`
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <AlertTriangle size={20} color={colors.error} />
                  <p style={{ color: colors.error, margin: 0, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    This action cannot be undone
                  </p>
                </div>
                
                <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  You are about to permanently delete:
                </p>
                <ul style={{ 
                  color: colors.textMuted, 
                  marginBottom: '1.5rem', 
                  fontSize: '0.9rem', 
                  paddingLeft: '1.5rem',
                  lineHeight: '1.6'
                }}>
                  <li>Your account and login credentials</li>
                  <li>Your company data <strong>(if you're the only member)</strong></li>
                  <li>All customers, jobs, quotes, and invoices</li>
                  <li>All team invitations and settings</li>
                  <li>All payment and subscription data</li>
                </ul>
                
                <div style={{
                  padding: '1rem',
                  background: `${colors.error}10`,
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ color: colors.text, marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: '500' }}>
                    To confirm deletion, type <strong style={{ color: colors.error }}>DELETE MY ACCOUNT</strong> below:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    disabled={deleting}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `2px solid ${deleteConfirmText === 'DELETE MY ACCOUNT' ? colors.error : colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    disabled={deleting}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontWeight: 'bold',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      minHeight: '44px',
                      opacity: deleting ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || deleting || !user?.id}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: deleteConfirmText === 'DELETE MY ACCOUNT' && !deleting ? colors.error : colors.bgInput,
                      border: `1px solid ${deleteConfirmText === 'DELETE MY ACCOUNT' && !deleting ? colors.error : colors.border}`,
                      borderRadius: '8px',
                      color: deleteConfirmText === 'DELETE MY ACCOUNT' && !deleting ? '#fff' : colors.textMuted,
                      fontWeight: 'bold',
                      cursor: deleteConfirmText === 'DELETE MY ACCOUNT' && !deleting ? 'pointer' : 'not-allowed',
                      opacity: deleting ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      minHeight: '44px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {deleting ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={18} />
                        Delete My Account Forever
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <TeamManagement companyId={companyId} userRole={userRole} />
      )}

      {activeTab === 'notifications' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Notification Preferences
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Configure how you receive notifications about jobs, messages, and payments.
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            maxWidth: '600px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <input type="checkbox" defaultChecked style={{ width: '20px', height: '20px' }} />
              <div>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  New Job Notifications
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Get notified when new jobs are created
                </div>
              </div>
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <input type="checkbox" defaultChecked style={{ width: '20px', height: '20px' }} />
              <div>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  Quote Status Updates
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Get notified when quotes are signed or rejected
                </div>
              </div>
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <input type="checkbox" style={{ width: '20px', height: '20px' }} />
              <div>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  Daily Summary
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Receive a daily summary of your schedule
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      {activeTab === 'business' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Business Settings
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Configure how your business operates
          </p>

          {/* Business Hours */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Business Hours
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Set your operating hours. The scheduling calendar will focus on these hours by default, with early/late hours collapsed but expandable.
            </p>

            <div style={{
              display: 'flex',
              gap: '1.5rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ color: colors.textMuted, fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                  Start Time
                </label>
                <input
                  type="time"
                  value={companySettings.business_hours_start || '08:00'}
                  onChange={(e) => saveCompanySettings({ business_hours_start: e.target.value })}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgCard,
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ color: colors.textMuted, fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                  End Time
                </label>
                <input
                  type="time"
                  value={companySettings.business_hours_end || '17:00'}
                  onChange={(e) => saveCompanySettings({ business_hours_end: e.target.value })}
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgCard,
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Customer Archive Rules */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 0.5rem 0',
              color: colors.text
            }}>
              Customer Auto-Archive
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Automatically archive old customers to keep your list clean. Archived customers don't show in the main list but can be restored anytime.
            </p>

            {/* Auto-archive inactive customers toggle */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              padding: '0.75rem 0',
              cursor: 'pointer',
            }}>
              <div>
                <div style={{ color: colors.text, fontWeight: 500 }}>Auto-archive inactive customers</div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.2rem' }}>
                  Archive customers with no jobs, quotes, or messages within the window below.
                </div>
              </div>
              <div
                role="switch"
                aria-checked={companySettings.auto_archive_enabled}
                onClick={() => saveCompanySettings({ auto_archive_enabled: !companySettings.auto_archive_enabled })}
                style={{
                  width: '48px', height: '28px',
                  background: companySettings.auto_archive_enabled ? colors.accent : colors.bgCard,
                  borderRadius: '999px', position: 'relative',
                  border: `1px solid ${colors.border}`,
                  transition: 'all 0.15s',
                  flexShrink: 0, cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '22px', height: '22px', background: '#fff',
                  borderRadius: '50%', position: 'absolute',
                  top: '2px',
                  left: companySettings.auto_archive_enabled ? '23px' : '2px',
                  transition: 'left 0.15s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </label>

            {companySettings.auto_archive_enabled && (
              <div style={{ marginTop: '0.75rem', marginLeft: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.4rem' }}>
                  Archive after inactive for (days)
                </label>
                <input
                  type="number"
                  min={30}
                  max={3650}
                  value={companySettings.auto_archive_inactive_days}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, auto_archive_inactive_days: parseInt(e.target.value) || 365 }))}
                  onBlur={(e) => saveCompanySettings({ auto_archive_inactive_days: parseInt(e.target.value) || 365 })}
                  style={{
                    width: '140px',
                    padding: '0.5rem 0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                  }}
                />
                <span style={{ marginLeft: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  (default 365)
                </span>
              </div>
            )}

            {/* Auto-archive lost leads toggle */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              padding: '0.75rem 0',
              marginTop: '0.75rem',
              borderTop: `1px solid ${colors.border}`,
              cursor: 'pointer',
            }}>
              <div>
                <div style={{ color: colors.text, fontWeight: 500 }}>Auto-archive stale leads</div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.2rem' }}>
                  Archive customers that never had a job within the window below from their creation date.
                </div>
              </div>
              <div
                role="switch"
                aria-checked={companySettings.auto_archive_lost_leads}
                onClick={() => saveCompanySettings({ auto_archive_lost_leads: !companySettings.auto_archive_lost_leads })}
                style={{
                  width: '48px', height: '28px',
                  background: companySettings.auto_archive_lost_leads ? colors.accent : colors.bgCard,
                  borderRadius: '999px', position: 'relative',
                  border: `1px solid ${colors.border}`,
                  transition: 'all 0.15s',
                  flexShrink: 0, cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '22px', height: '22px', background: '#fff',
                  borderRadius: '50%', position: 'absolute',
                  top: '2px',
                  left: companySettings.auto_archive_lost_leads ? '23px' : '2px',
                  transition: 'left 0.15s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </label>

            {companySettings.auto_archive_lost_leads && (
              <div style={{ marginTop: '0.75rem', marginLeft: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.4rem' }}>
                  Archive if no job within (days)
                </label>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={companySettings.auto_archive_lost_lead_days}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, auto_archive_lost_lead_days: parseInt(e.target.value) || 90 }))}
                  onBlur={(e) => saveCompanySettings({ auto_archive_lost_lead_days: parseInt(e.target.value) || 90 })}
                  style={{
                    width: '140px',
                    padding: '0.5rem 0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                  }}
                />
                <span style={{ marginLeft: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  (default 90)
                </span>
              </div>
            )}
          </div>

          {/* Field Worker Title */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 0.5rem 0',
              color: colors.text
            }}>
              Field Worker Title
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              What do you call your field workers? This label is used throughout the app.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {['Technician', 'Specialist', 'Operator', 'Inspector', 'Installer', 'Service Pro', 'Field Rep'].map(title => (
                <button
                  key={title}
                  onClick={() => saveCompanySettings({ field_worker_title: title })}
                  disabled={saving}
                  style={{
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    border: `1px solid ${companySettings.field_worker_title === title ? colors.accent : colors.border}`,
                    background: companySettings.field_worker_title === title ? colors.accent + '20' : colors.bgCard,
                    color: companySettings.field_worker_title === title ? colors.accent : colors.textSecondary,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    fontWeight: companySettings.field_worker_title === title ? '600' : '400'
                  }}
                >
                  {title}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={companySettings.field_worker_title || ''}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, field_worker_title: e.target.value }))}
              onBlur={(e) => saveCompanySettings({ field_worker_title: e.target.value || 'Technician' })}
              placeholder="Custom title..."
              disabled={saving}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '0.75rem',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.bgCard,
                color: colors.text,
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Company Logo Upload */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Company Logo
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Upload your company logo to display in the app header. Recommended size: 200x200 pixels or larger.
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              flexWrap: 'wrap'
            }}>
              {/* Current Logo Preview */}
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '12px',
                background: colors.bgCard,
                border: `2px dashed ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {companySettings.logo_url ? (
                  <>
                    <img
                      src={companySettings.logo_url}
                      alt="Company Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                    <button
                      onClick={removeLogo}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: colors.error,
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <div style={{ color: colors.textMuted, fontSize: '0.8rem', textAlign: 'center' }}>
                    No logo
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={logoUploading || saving}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: colors.accent,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: logoUploading || saving ? 'not-allowed' : 'pointer',
                    opacity: logoUploading || saving ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Upload size={18} />
                  {logoUploading ? 'Uploading...' : 'Upload Logo'}
                </button>
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                  Max 2MB • JPEG, PNG, GIF, WebP, SVG
                </div>
              </div>
            </div>
          </div>

          {/* Completion Signature */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 0.5rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              ✍️ Job Completion Signature
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Capture customer signature when completing a job. Useful for proof of work completion.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.completion_signature_mode === 'required' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.completion_signature_mode === 'required' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="completion_signature_mode"
                  value="required"
                  checked={companySettings.completion_signature_mode === 'required'}
                  onChange={() => saveCompanySettings({ completion_signature_mode: 'required' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Required
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Customer signature must be captured before completing a job.
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.completion_signature_mode === 'optional' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.completion_signature_mode === 'optional' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="completion_signature_mode"
                  value="optional"
                  checked={companySettings.completion_signature_mode === 'optional'}
                  onChange={() => saveCompanySettings({ completion_signature_mode: 'optional' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Optional (default)
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Signature pad is shown but can be skipped.
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.completion_signature_mode === 'disabled' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.completion_signature_mode === 'disabled' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="completion_signature_mode"
                  value="disabled"
                  checked={companySettings.completion_signature_mode === 'disabled'}
                  onChange={() => saveCompanySettings({ completion_signature_mode: 'disabled' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Disabled
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    No signature capture on job completion.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Auto-Email Receipts */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Mail size={20} color="#3498db" />
              Auto-Email Receipts
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Automatically email a receipt to customers when a job is completed and payment is collected. Requires the customer to have an email on file.
            </p>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: saving ? 'not-allowed' : 'pointer',
              gap: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Send receipt on job completion
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {companySettings.auto_email_receipts
                    ? 'Receipts will be emailed automatically after payment is collected'
                    : 'Receipts will not be sent automatically'}
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ auto_email_receipts: !companySettings.auto_email_receipts })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.auto_email_receipts ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.auto_email_receipts ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.auto_email_receipts ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>
          </div>

          {/* Auto-Bill Recurring Jobs */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <CreditCard size={20} color="#e67e22" />
              Auto-Bill Recurring Jobs
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Automatically charge the customer's card on file when a recurring job is completed. Only applies to recurring job instances with a saved payment method.
            </p>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: saving ? 'not-allowed' : 'pointer',
              gap: '1rem',
              marginBottom: companySettings.auto_bill_recurring ? '1.5rem' : 0
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Auto-bill on completion
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {companySettings.auto_bill_recurring
                    ? 'Recurring jobs will be auto-billed when completed'
                    : 'Recurring jobs require manual payment collection'}
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ auto_bill_recurring: !companySettings.auto_bill_recurring })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.auto_bill_recurring ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.auto_bill_recurring ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.auto_bill_recurring ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {companySettings.auto_bill_recurring && (
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem'
                }}>
                  Receipt Email for Auto-Billed Jobs
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { value: 'always', label: 'Always send receipt', desc: 'Customer gets a receipt every time their card is charged' },
                    { value: 'global', label: 'Use global setting', desc: 'Follow the Auto-Email Receipts toggle above' },
                    { value: 'never', label: 'Never send receipt', desc: 'No receipt email on auto-billed jobs' }
                  ].map(option => (
                    <label key={option.value} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      background: companySettings.auto_bill_receipt_mode === option.value ? `${colors.accent}15` : 'transparent',
                      border: companySettings.auto_bill_receipt_mode === option.value ? `1px solid ${colors.accent}50` : '1px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="auto_bill_receipt_mode"
                        value={option.value}
                        checked={companySettings.auto_bill_receipt_mode === option.value}
                        onChange={() => saveCompanySettings({ auto_bill_receipt_mode: option.value })}
                        disabled={saving}
                        style={{ marginTop: '0.25rem' }}
                      />
                      <div>
                        <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.15rem', fontSize: '0.9rem' }}>
                          {option.label}
                        </div>
                        <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                          {option.desc}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Invoice Template */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FileText size={20} color="#1abc9c" />
              Invoice Template
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Customize your invoice defaults. These apply to all new invoices.
            </p>

            {/* Payment Terms */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                color: colors.text,
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                Default Payment Terms
              </label>
              <select
                value={companySettings.invoice_payment_terms}
                onChange={(e) => saveCompanySettings({ invoice_payment_terms: e.target.value })}
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              >
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
              </select>
            </div>

            {/* Custom Notes */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                color: colors.text,
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                Default Invoice Notes
              </label>
              <textarea
                value={companySettings.invoice_notes}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, invoice_notes: e.target.value }))}
                onBlur={(e) => saveCompanySettings({ invoice_notes: e.target.value })}
                placeholder="e.g., Thank you for your business!"
                disabled={saving}
                rows={2}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Custom Footer */}
            <div>
              <label style={{
                display: 'block',
                color: colors.text,
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                Invoice Footer Text
              </label>
              <textarea
                value={companySettings.invoice_footer_text}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, invoice_footer_text: e.target.value }))}
                onBlur={(e) => saveCompanySettings({ invoice_footer_text: e.target.value })}
                placeholder="e.g., Licensed & Insured | Satisfaction Guaranteed"
                disabled={saving}
                rows={2}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Recurring Jobs */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <RefreshCw size={20} color="#9b59b6" />
              Recurring Jobs
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Enable recurring job scheduling for regular maintenance services like pool cleaning, pest control, or lawn care.
            </p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <div>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Enable Recurring Jobs
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Create jobs that automatically repeat weekly, bi-weekly, or monthly
                </div>
              </div>
              <div
                onClick={async () => {
                  if (saving) return;
                  const newValue = !companySettings.recurring_enabled;

                  // If trying to enable but not on a recurring plan, prompt to upgrade
                  if (newValue && !companyInfo.isRecurring) {
                    const shouldUpgrade = await showConfirm(
                      'Recurring job scheduling requires a recurring service plan. Would you like to upgrade your plan?',
                      { title: 'Upgrade Required', confirmText: 'Go to Plans', confirmStyle: 'success' }
                    );
                    if (shouldUpgrade) {
                      setActiveTab('subscription');
                    }
                    return;
                  }

                  if (!newValue && companySettings.recurring_enabled) {
                    // Turning off - warn about losing recurring jobs
                    const confirmed = await showConfirm(
                      'Disabling recurring jobs will stop all future recurring job instances from being generated. Existing scheduled jobs will remain, but no new recurring instances will be created. Continue?',
                      { title: 'Disable Recurring Jobs?', confirmText: 'Disable', confirmStyle: 'danger' }
                    );
                    if (!confirmed) return;
                  }
                  saveCompanySettings({ recurring_enabled: newValue });
                }}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.recurring_enabled ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.recurring_enabled ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.recurring_enabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {companySettings.recurring_enabled && companyInfo.isRecurring && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#9b59b620',
                border: '1px solid #9b59b650',
                borderRadius: '8px',
                color: '#9b59b6',
                fontSize: '0.9rem'
              }}>
                <strong>Active!</strong> When creating or editing jobs, you'll now see options to make them recurring. Recurring jobs will automatically generate new instances based on your schedule.
              </div>
            )}

            {!companyInfo.isRecurring && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.textMuted,
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <span>Recurring scheduling is available on recurring service plans.</span>
                <button
                  onClick={() => setActiveTab('subscription')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#9b59b6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  View Plans
                </button>
              </div>
            )}

            {/* Use Cases */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px'
            }}>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                Great for:
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '0.5rem'
              }}>
                {['Pool maintenance', 'Pest control', 'Lawn care', 'HVAC service', 'Cleaning services'].map(item => (
                  <div key={item} style={{
                    padding: '0.5rem 0.75rem',
                    background: colors.bgInput,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: colors.text
                  }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Advanced Quoting Settings */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Advanced Quoting
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Customize how quotes are built for your specific business type.
            </p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <div>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Enable Advanced Quoting
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Access business-specific quoting templates and custom fields
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ advanced_quoting_enabled: !companySettings.advanced_quoting_enabled })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.advanced_quoting_enabled ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.advanced_quoting_enabled ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.advanced_quoting_enabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {companySettings.advanced_quoting_enabled && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#3498db20',
                border: '1px solid #3498db50',
                borderRadius: '8px',
                color: '#3498db',
                fontSize: '0.9rem'
              }}>
                <strong>Active!</strong> Your quote builder now includes custom templates and fields tailored for {companyInfo.business_type?.replace('_', ' ') || 'your'} businesses.
              </div>
            )}

            {/* Business-specific tips */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px'
            }}>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                Advanced quoting includes:
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.5rem'
              }}>
                {['Custom field templates', 'Room/area-based pricing', 'Measurement calculators', 'Material estimators', 'Labor hour tracking'].map(item => (
                  <div key={item} style={{
                    padding: '0.5rem 0.75rem',
                    background: colors.bgInput,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: colors.text
                  }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Product Settings */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📦 Product Settings
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Simplify product management for your team
            </p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <div>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Show SKU Code Field
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  When disabled, products only need a name. SKU codes are auto-generated.
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ sku_code_visible: !companySettings.sku_code_visible })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.sku_code_visible ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.sku_code_visible ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.sku_code_visible ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {!companySettings.sku_code_visible && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: `${colors.success}20`,
                border: `1px solid ${colors.success}50`,
                borderRadius: '8px',
                color: colors.success,
                fontSize: '0.9rem'
              }}>
                ✓ <strong>Simplified Mode:</strong> Just enter product names. SKU codes like "SPRING-REP-001" are created automatically.
              </div>
            )}
          </div>

          {/* Offline Mode Section */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Offline Mode
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Download your data for use without internet. This data is stored on this device only.
            </p>

            {/* Toggle */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: cacheWarming ? 'not-allowed' : 'pointer',
              marginBottom: '1rem'
            }}>
              <div>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  Enable Offline Mode
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Download your data for use without internet
                </div>
              </div>
              <div
                onClick={() => !cacheWarming && handleToggleOfflineMode(!offlineEnabled)}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: offlineEnabled ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: cacheWarming ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${offlineEnabled ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: offlineEnabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {/* Cache warming progress */}
            {cacheWarming && (
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Downloading data... ({cacheProgress.current}/{cacheProgress.total})
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: colors.border,
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${cacheProgress.total > 0 ? (cacheProgress.current / cacheProgress.total) * 100 : 0}%`,
                    height: '100%',
                    background: colors.accent,
                    borderRadius: '4px',
                    transition: 'width 0.3s'
                  }} />
                </div>
                {cacheProgress.table && (
                  <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    Cached: {cacheProgress.table}
                  </div>
                )}
              </div>
            )}

            {/* Cache info panel */}
            {offlineEnabled && !cacheWarming && cacheSummary && (
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.75rem' }}>
                  Cached Data ({cacheSize} MB)
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  {Object.entries(cacheSummary).map(([table, info]) => (
                    <div key={table} style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: colors.text }}>
                        {info.rows}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                        {table.replace(/_/g, ' ')}
                      </div>
                      {info.cachedAt && (
                        <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                          {new Date(info.cachedAt).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Refresh and Clear buttons */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleRefreshCache}
                    disabled={cacheWarming}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RefreshCw size={16} />
                    Refresh Cache
                  </button>
                  <button
                    onClick={handleClearCache}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'transparent',
                      color: '#e74c3c',
                      border: '1px solid #e74c3c',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Trash2 size={16} />
                    Clear Cache
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            Business Documents
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Store important business documents like proof of insurance, licenses, and certifications.
          </p>

          {/* Upload Section */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            border: `1px dashed ${colors.border}`
          }}>
            <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
              Upload New Document
            </h3>

            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              {/* Document Type */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Document Type
                </label>
                <select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  {documentTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.icon} {type.label}</option>
                  ))}
                </select>
              </div>

              {/* Expiration Date (optional) */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Expiration Date <span style={{ fontWeight: 'normal', color: colors.textMuted }}>(optional)</span>
                </label>
                <input
                  type="date"
                  value={docExpirationDate}
                  onChange={(e) => setDocExpirationDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            {/* Description */}
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                Description <span style={{ fontWeight: 'normal', color: colors.textMuted }}>(optional)</span>
              </label>
              <input
                type="text"
                value={docDescription}
                onChange={(e) => setDocDescription(e.target.value)}
                placeholder="e.g., 2025 General Liability Policy"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* File Upload */}
            <div style={{ marginTop: '1.5rem' }}>
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDoc}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: uploadingDoc ? 'not-allowed' : 'pointer',
                  opacity: uploadingDoc ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minHeight: '44px'
                }}
              >
                <Upload size={18} />
                {uploadingDoc ? 'Uploading...' : 'Choose File & Upload'}
              </button>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Supported: PDF, Word docs, Images (max 10MB)
              </p>
            </div>
          </div>

          {/* Documents List */}
          <div>
            <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
              Stored Documents ({documents.length})
            </h3>

            {documentsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                Loading documents...
              </div>
            ) : documents.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                background: colors.bgInput,
                borderRadius: '12px',
                color: colors.textMuted
              }}>
                <FolderOpen size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>No documents uploaded yet</p>
                <p style={{ fontSize: '0.9rem' }}>Upload your first document above</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {documents.map(doc => {
                  const docType = documentTypes.find(t => t.id === doc.document_type);
                  const expired = isExpired(doc.expiration_date);
                  const expiringSoon = isExpiringSoon(doc.expiration_date);

                  return (
                    <div
                      key={doc.id}
                      style={{
                        background: colors.bgInput,
                        borderRadius: '12px',
                        padding: '1rem 1.25rem',
                        border: `1px solid ${expired ? colors.danger : expiringSoon ? '#f39c12' : colors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap'
                      }}
                    >
                      {/* Icon */}
                      <div style={{ fontSize: '1.5rem' }}>
                        {docType?.icon || '📄'}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                          {doc.file_name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                          {docType?.label || doc.document_type} • {formatFileSize(doc.file_size)}
                          {doc.description && ` • ${doc.description}`}
                        </div>
                        {doc.expiration_date && (
                          <div style={{
                            fontSize: '0.85rem',
                            color: expired ? colors.danger : expiringSoon ? '#f39c12' : colors.textMuted,
                            marginTop: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}>
                            <Calendar size={14} />
                            {expired ? 'Expired: ' : expiringSoon ? 'Expires soon: ' : 'Expires: '}
                            {new Date(doc.expiration_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      {/* Status badges */}
                      {expired && (
                        <span style={{
                          background: colors.danger,
                          color: '#fff',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          EXPIRED
                        </span>
                      )}
                      {expiringSoon && !expired && (
                        <span style={{
                          background: '#f39c12',
                          color: '#000',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          EXPIRING SOON
                        </span>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleViewDocument(doc)}
                          style={{
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '44px',
                            minHeight: '44px'
                          }}
                          title="View Document"
                        >
                          <Eye size={18} style={{ color: colors.text }} />
                        </button>
                        {(userRole === 'owner' || userRole === 'admin') && (
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            style={{
                              padding: '0.5rem',
                              background: colors.bgCard,
                              border: `1px solid ${colors.danger}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '44px',
                              minHeight: '44px'
                            }}
                            title="Delete Document"
                          >
                            <Trash2 size={18} style={{ color: colors.danger }} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'quotes' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Quote Customization
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Customize how your quotes and signed PDFs appear to customers
          </p>

          {/* Company Info for Quotes */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem 0', color: colors.text }}>
              Company Information on Quotes
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              This information appears in the header of your quotes and signed PDFs
            </p>

            <div style={{ display: 'grid', gap: '1rem', maxWidth: '600px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Company Name
                </label>
                <input
                  type="text"
                  value={companySettings.quote_company_name}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_company_name: e.target.value }))}
                  onBlur={(e) => saveCompanySettings({ quote_company_name: e.target.value })}
                  placeholder="Your Company Name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={companySettings.quote_company_address}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_company_address: e.target.value }))}
                  onBlur={(e) => saveCompanySettings({ quote_company_address: e.target.value })}
                  placeholder="123 Main St, City, State 12345"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                    Phone
                  </label>
                  <input
                    type="text"
                    value={companySettings.quote_company_phone}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_company_phone: e.target.value }))}
                    onBlur={(e) => saveCompanySettings({ quote_company_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={companySettings.quote_company_email}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_company_email: e.target.value }))}
                    onBlur={(e) => saveCompanySettings({ quote_company_email: e.target.value })}
                    placeholder="info@company.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}>
                <input
                  type="checkbox"
                  checked={companySettings.quote_show_logo}
                  onChange={(e) => saveCompanySettings({ quote_show_logo: e.target.checked })}
                  disabled={saving}
                  style={{ width: '20px', height: '20px' }}
                />
                <div>
                  <div style={{ color: colors.text, fontWeight: '500' }}>Show company logo on quotes</div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    {companySettings.logo_url ? 'Your uploaded logo will appear in the quote header' : 'Upload a logo in Business settings first'}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Sales Tax Configuration */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Sales Tax Settings
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Configure how sales tax is handled on quotes and invoices
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {/* Required Option */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.sales_tax_mode === 'required' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.sales_tax_mode === 'required' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="sales_tax_mode"
                  value="required"
                  checked={companySettings.sales_tax_mode === 'required'}
                  onChange={() => saveCompanySettings({ sales_tax_mode: 'required' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Require Sales Tax
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Sales tax is always added to quotes and invoices. Tax cannot be removed by users.
                  </div>
                </div>
              </label>

              {/* Optional */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.sales_tax_mode === 'optional' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.sales_tax_mode === 'optional' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="sales_tax_mode"
                  value="optional"
                  checked={companySettings.sales_tax_mode === 'optional'}
                  onChange={() => saveCompanySettings({ sales_tax_mode: 'optional' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Optional Sales Tax
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Sales tax can be toggled on or off for each quote or invoice. Default is on.
                  </div>
                </div>
              </label>

              {/* Disabled */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                background: companySettings.sales_tax_mode === 'disabled' ? `${colors.success}20` : colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer',
                border: companySettings.sales_tax_mode === 'disabled' ? `2px solid ${colors.success}` : '2px solid transparent'
              }}>
                <input
                  type="radio"
                  name="sales_tax_mode"
                  value="disabled"
                  checked={companySettings.sales_tax_mode === 'disabled'}
                  onChange={() => saveCompanySettings({ sales_tax_mode: 'disabled' })}
                  disabled={saving}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    Disable Sales Tax
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    No sales tax is shown or collected. Use this if your services are tax-exempt.
                  </div>
                </div>
              </label>
            </div>

            {/* Info Box */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: `${colors.accent}20`,
              border: `1px solid ${colors.accent}50`,
              borderRadius: '8px',
              color: colors.accent,
              fontSize: '0.9rem'
            }}>
              💡 <strong>Note:</strong> This setting affects all new quotes and invoices. Existing documents will not be changed.
            </div>
          </div>

          {/* Custom Terms */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem 0', color: colors.text }}>
              Custom Terms & Conditions
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Add your own terms and conditions that appear on quotes. Leave blank to use default terms.
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                Terms Mode
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { value: 'required', label: 'Always Include', desc: 'Terms always appear on quotes' },
                  { value: 'optional_on', label: 'Optional (Default On)', desc: 'Included by default, can be removed per quote' },
                  { value: 'optional_off', label: 'Optional (Default Off)', desc: 'Not included by default, can be added per quote' },
                  { value: 'disabled', label: 'Never Include', desc: 'Terms never appear on quotes' }
                ].map(option => (
                  <label key={option.value} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: companySettings.quote_terms_mode === option.value ? `${colors.success}20` : colors.bgCard,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: companySettings.quote_terms_mode === option.value ? `1px solid ${colors.success}` : '1px solid transparent'
                  }}>
                    <input
                      type="radio"
                      name="quote_terms_mode"
                      value={option.value}
                      checked={companySettings.quote_terms_mode === option.value}
                      onChange={() => saveCompanySettings({ quote_terms_mode: option.value })}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <div>
                      <div style={{ color: colors.text, fontWeight: '500' }}>{option.label}</div>
                      <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                Custom Terms Text
              </label>
              <textarea
                value={companySettings.quote_custom_terms}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_custom_terms: e.target.value }))}
                onBlur={(e) => saveCompanySettings({ quote_custom_terms: e.target.value })}
                placeholder="Enter your terms and conditions here. Each line will be displayed as a bullet point."
                rows={6}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '0.9rem',
                  resize: 'vertical',
                  lineHeight: '1.5'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '0.75rem',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                  Tip: Put each term on a new line for better formatting
                </div>
                <button
                  onClick={() => setShowTermsQuestionnaire(true)}
                  disabled={generatingTerms}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: generatingTerms ? colors.bgInput : `linear-gradient(135deg, ${colors.accent}, #9333ea)`,
                    color: generatingTerms ? colors.textMuted : '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    cursor: generatingTerms ? 'not-allowed' : 'pointer',
                    opacity: generatingTerms ? 0.7 : 1
                  }}
                >
                  <Sparkles size={16} style={{ animation: generatingTerms ? 'spin 1s linear infinite' : 'none' }} />
                  {generatingTerms ? 'Generating...' : 'Open AI Terms Generator'}
                </button>
              </div>

              {/* T&C Questionnaire Modal */}
              {showTermsQuestionnaire && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1.25rem',
                  background: colors.bgCard,
                  border: `2px solid ${colors.accent}`,
                  borderRadius: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Sparkles size={18} color={colors.accent} />
                      Customize Your Terms & Conditions
                    </h4>
                    <button
                      onClick={() => setShowTermsQuestionnaire(false)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
                    >
                      <X size={20} color={colors.textMuted} />
                    </button>
                  </div>

                  {/* AI Disclaimer */}
                  <div style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    background: `${colors.warning || '#f59e0b'}15`,
                    border: `1px solid ${colors.warning || '#f59e0b'}40`,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <AlertTriangle size={18} color={colors.warning || '#f59e0b'} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.5 }}>
                        <strong style={{ color: colors.text }}>AI-Generated Content:</strong> These terms will be generated using artificial intelligence based on your selections below.
                        WorkVan is not responsible for the accuracy, completeness, or legal enforceability of AI-generated content.
                        <strong style={{ color: colors.text }}> You should review the generated terms and consult with a legal professional before using them in your business.</strong>
                      </div>
                    </div>
                  </div>

                  {/* Cancellation Policy Section */}
                  <div style={{ marginBottom: '1.25rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 0.75rem 0', color: colors.text, fontSize: '0.95rem' }}>Cancellation Policy</h5>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        How much notice is required to cancel an appointment?
                      </label>
                      <select
                        value={termsQuestionnaireAnswers.cancellation_notice_hours}
                        onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, cancellation_notice_hours: parseInt(e.target.value) }))}
                        style={{ width: '100%', padding: '0.6rem', background: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '6px' }}
                      >
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours (3 days)</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={termsQuestionnaireAnswers.early_termination_fee}
                          onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, early_termination_fee: e.target.checked }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ color: colors.text, fontSize: '0.9rem' }}>Charge early termination fees for recurring contracts?</span>
                      </label>
                      {termsQuestionnaireAnswers.early_termination_fee && (
                        <input
                          type="text"
                          value={termsQuestionnaireAnswers.early_termination_fee_amount}
                          onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, early_termination_fee_amount: e.target.value }))}
                          placeholder="e.g., 2 months service fee, $150, etc."
                          style={{ width: '100%', marginTop: '0.5rem', padding: '0.6rem', background: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '6px' }}
                        />
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        Refund policy for prepaid services?
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {[{ value: 'full', label: 'Full refund' }, { value: 'prorated', label: 'Prorated' }, { value: 'none', label: 'No refunds' }].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setTermsQuestionnaireAnswers(prev => ({ ...prev, refund_policy: opt.value }))}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              padding: '0.5rem',
                              background: termsQuestionnaireAnswers.refund_policy === opt.value ? colors.accent : colors.bgCard,
                              color: termsQuestionnaireAnswers.refund_policy === opt.value ? '#fff' : colors.text,
                              border: `1px solid ${termsQuestionnaireAnswers.refund_policy === opt.value ? colors.accent : colors.border}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Service Guarantees Section */}
                  <div style={{ marginBottom: '1.25rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 0.75rem 0', color: colors.text, fontSize: '0.95rem' }}>Service Guarantees</h5>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        Warranty period on workmanship?
                      </label>
                      <select
                        value={termsQuestionnaireAnswers.warranty_period_months}
                        onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, warranty_period_months: parseInt(e.target.value) }))}
                        style={{ width: '100%', padding: '0.6rem', background: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '6px' }}
                      >
                        <option value={0}>No warranty</option>
                        <option value={3}>3 months</option>
                        <option value={6}>6 months</option>
                        <option value={12}>1 year</option>
                        <option value={24}>2 years</option>
                        <option value={60}>5 years</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={termsQuestionnaireAnswers.satisfaction_guarantee}
                          onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, satisfaction_guarantee: e.target.checked }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ color: colors.text, fontSize: '0.9rem' }}>Offer satisfaction guarantee?</span>
                      </label>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        Re-service policy if customer is unsatisfied?
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {[{ value: 'free', label: 'Free re-service' }, { value: 'discounted', label: 'Discounted' }, { value: 'none', label: 'No re-service' }].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setTermsQuestionnaireAnswers(prev => ({ ...prev, reservice_policy: opt.value }))}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              padding: '0.5rem',
                              background: termsQuestionnaireAnswers.reservice_policy === opt.value ? colors.accent : colors.bgCard,
                              color: termsQuestionnaireAnswers.reservice_policy === opt.value ? '#fff' : colors.text,
                              border: `1px solid ${termsQuestionnaireAnswers.reservice_policy === opt.value ? colors.accent : colors.border}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Contract Renewal Section */}
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 0.75rem 0', color: colors.text, fontSize: '0.95rem' }}>Contract Renewal</h5>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={termsQuestionnaireAnswers.auto_renewal}
                        onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, auto_renewal: e.target.checked }))}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span style={{ color: colors.text, fontSize: '0.9rem' }}>Auto-renew contracts (with advance notice to cancel)?</span>
                    </label>
                  </div>

                  {/* Custom Flavor Section */}
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 0.5rem 0', color: colors.text, fontSize: '0.95rem' }}>Additional Instructions (Optional)</h5>
                    <p style={{ margin: '0 0 0.75rem 0', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Add any specific language, tone preferences, or additional clauses you'd like included.
                    </p>
                    <textarea
                      value={termsQuestionnaireAnswers.custom_flavor}
                      onChange={(e) => setTermsQuestionnaireAnswers(prev => ({ ...prev, custom_flavor: e.target.value }))}
                      placeholder="e.g., 'Keep the tone friendly and professional', 'Add a clause about weather delays', 'Mention our 100% satisfaction promise'..."
                      style={{
                        width: '100%',
                        minHeight: '70px',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowTermsQuestionnaire(false)}
                      style={{
                        padding: '0.6rem 1.25rem',
                        background: colors.bgInput,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleQuestionnaireComplete}
                      style={{
                        padding: '0.6rem 1.25rem',
                        background: `linear-gradient(135deg, ${colors.accent}, #9333ea)`,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Sparkles size={16} />
                      Generate Terms
                    </button>
                  </div>
                </div>
              )}

              {/* AI Prompt Modal */}
              {showAIPrompt && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: colors.bgCard,
                  border: `2px solid ${colors.accent}`,
                  borderRadius: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem'
                  }}>
                    <h4 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Sparkles size={18} color={colors.accent} />
                      AI Instructions
                    </h4>
                    <button
                      onClick={() => { setShowAIPrompt(false); setAiPromptText(''); }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      <X size={20} color={colors.textMuted} />
                    </button>
                  </div>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    Optional: Tell the AI what you'd like in your terms (e.g., "focus on warranty details", "keep it short", "include deposit requirements")
                  </p>
                  <textarea
                    value={aiPromptText}
                    onChange={(e) => setAiPromptText(e.target.value)}
                    placeholder="e.g., Include a 2-year warranty, require 50% deposit, keep terms brief and friendly..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      marginBottom: '0.75rem'
                    }}
                  />
                  {companyInfo.business_type && (
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      background: `${colors.accent}15`,
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      color: colors.textMuted,
                      marginBottom: '0.75rem'
                    }}>
                      Using company profile: <strong style={{ color: colors.text }}>{companyInfo.name || 'Your Company'}</strong> ({companyInfo.business_type.replace('_', ' ')}{companyInfo.isRecurring ? ', recurring services' : ''})
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setShowAIPrompt(false); setAiPromptText(''); }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: colors.bgInput,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => generateTermsWithAI(aiPromptText)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: `linear-gradient(135deg, ${colors.accent}, #9333ea)`,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Sparkles size={16} />
                      Generate Terms
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Footer */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem 0', color: colors.text }}>
              Custom Footer Text
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Add a thank you message, warranty info, or other text at the bottom of quotes
            </p>

            <textarea
              value={companySettings.quote_custom_footer}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, quote_custom_footer: e.target.value }))}
              onBlur={(e) => saveCompanySettings({ quote_custom_footer: e.target.value })}
              placeholder="Thank you for your business! We appreciate your trust in our services."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.9rem',
                resize: 'vertical',
                lineHeight: '1.5'
              }}
            />
          </div>

          {/* Tech Permissions Section - Team+ only */}
          {isTeamPlus && (
            <div style={{
              marginTop: '2rem',
              background: colors.bgInput,
              borderRadius: '12px',
              padding: '2rem',
              border: `1px solid ${colors.border}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.5rem'
              }}>
                <Shield size={24} color={colors.accent} />
                <h3 style={{ fontSize: '1.25rem', margin: 0, color: colors.text }}>
                  Tech Permissions
                </h3>
              </div>
              <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
                Control what your technicians can do when creating quotes in the field
              </p>

              {/* Quoting Mode */}
              <div style={{
                background: colors.bgCard,
                padding: '1.5rem',
                borderRadius: '12px',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1.1rem', margin: '0 0 0.5rem 0', color: colors.text }}>
                  Quoting Mode
                </h4>
                <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  How strict should pricing be when techs create quotes?
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    { value: 'flexible', label: 'Flexible', desc: 'Techs can quote anything - manual items, custom prices, full control' },
                    { value: 'guided', label: 'Guided', desc: 'Techs use price list but can add manual items and adjust prices' },
                    { value: 'strict', label: 'Strict', desc: 'Techs can only quote items from the price list at set prices' }
                  ].map(option => (
                    <label key={option.value} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1rem',
                      background: techPermissions.tech_quoting_mode === option.value ? `${colors.accent}20` : colors.bgInput,
                      borderRadius: '8px',
                      cursor: savingPermissions ? 'not-allowed' : 'pointer',
                      border: techPermissions.tech_quoting_mode === option.value ? `2px solid ${colors.accent}` : '2px solid transparent',
                      opacity: savingPermissions ? 0.7 : 1
                    }}>
                      <input
                        type="radio"
                        name="quoting_mode"
                        value={option.value}
                        checked={techPermissions.tech_quoting_mode === option.value}
                        onChange={() => saveTechPermission('tech_quoting_mode', option.value)}
                        disabled={savingPermissions}
                        style={{ marginTop: '0.25rem' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                          {option.label}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                          {option.desc}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Permission Toggles */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {/* Manual Items */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1.25rem',
                  background: colors.bgCard,
                  borderRadius: '12px',
                  cursor: savingPermissions ? 'not-allowed' : 'pointer',
                  opacity: savingPermissions ? 0.7 : 1
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                      Add Manual Items
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                      Techs can add items not in the price list
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={techPermissions.tech_can_add_manual_items}
                    onChange={(e) => saveTechPermission('tech_can_add_manual_items', e.target.checked)}
                    disabled={savingPermissions}
                    style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                  />
                </label>

                {/* Modify Prices */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1.25rem',
                  background: colors.bgCard,
                  borderRadius: '12px',
                  cursor: savingPermissions ? 'not-allowed' : 'pointer',
                  opacity: savingPermissions ? 0.7 : 1
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                      Modify Prices
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                      Techs can change prices on quoted items
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={techPermissions.tech_can_modify_prices}
                    onChange={(e) => saveTechPermission('tech_can_modify_prices', e.target.checked)}
                    disabled={savingPermissions}
                    style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                  />
                </label>

                {/* Create Service Plans (recurring only) */}
                {companySettings.recurring_billing_enabled && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1.25rem',
                    background: colors.bgCard,
                    borderRadius: '12px',
                    cursor: savingPermissions ? 'not-allowed' : 'pointer',
                    opacity: savingPermissions ? 0.7 : 1
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                        Create Service Plans
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                        Techs can sign customers up for recurring plans
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={techPermissions.tech_can_create_service_plans}
                      onChange={(e) => saveTechPermission('tech_can_create_service_plans', e.target.checked)}
                      disabled={savingPermissions}
                      style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                    />
                  </label>
                )}
              </div>

              {/* Max Discount */}
              <div style={{
                background: colors.bgCard,
                padding: '1.5rem',
                borderRadius: '12px'
              }}>
                <h4 style={{ fontSize: '1.1rem', margin: '0 0 0.5rem 0', color: colors.text }}>
                  Maximum Discount
                </h4>
                <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  The maximum discount percentage techs can apply to a quote
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={techPermissions.tech_max_discount_percent}
                      onChange={(e) => setTechPermissions(prev => ({ ...prev, tech_max_discount_percent: parseInt(e.target.value) }))}
                      onMouseUp={(e) => saveTechPermission('tech_max_discount_percent', parseInt(e.target.value))}
                      onTouchEnd={(e) => saveTechPermission('tech_max_discount_percent', parseInt(e.target.value))}
                      disabled={savingPermissions}
                      style={{ width: '100%', cursor: 'pointer' }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: colors.textMuted,
                      marginTop: '0.25rem'
                    }}>
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div style={{
                    minWidth: '100px',
                    padding: '1rem',
                    background: colors.bgInput,
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.accent }}>
                      {techPermissions.tech_max_discount_percent}%
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                      {techPermissions.tech_max_discount_percent === 100 ? 'Unlimited' : 'Max Discount'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Time Slots */}
          <div style={{
            background: colors.bgInput,
            padding: isMobile ? '1rem' : '1.5rem',
            borderRadius: '12px',
            marginTop: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem 0', color: colors.text }}>
              Calendar Time Slots
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Choose the time slot granularity for scheduling jobs in the visual calendar
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {[
                { value: 15, label: '15 min' },
                { value: 30, label: '30 min' },
                { value: 60, label: '1 hour' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => saveCompanySettings({ calendar_slot_minutes: option.value })}
                  disabled={saving}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: companySettings.calendar_slot_minutes === option.value
                      ? colors.accent
                      : colors.bgCard,
                    color: companySettings.calendar_slot_minutes === option.value
                      ? '#fff'
                      : colors.text,
                    border: `1px solid ${companySettings.calendar_slot_minutes === option.value ? colors.accent : colors.border}`,
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: companySettings.calendar_slot_minutes === option.value ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview Info */}
          <div style={{
            padding: '1rem',
            marginTop: '2rem',
            background: `${colors.accent}20`,
            border: `1px solid ${colors.accent}50`,
            borderRadius: '8px',
            color: colors.accent,
            fontSize: '0.9rem'
          }}>
            💡 <strong>Tip:</strong> Create a test quote and sign it to see how your customizations look on the final PDF.
          </div>
        </div>
      )}

      {activeTab === 'communications' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem 0', color: colors.text }}>
                Message Templates
              </h2>
              <p style={{ color: colors.textMuted, margin: 0 }}>
                Create reusable SMS and email templates for quick communication
              </p>
            </div>
            <button
              onClick={() => openTemplateModal()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              <Plus size={18} />
              New Template
            </button>
          </div>

          {/* Filter Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'sms', label: 'SMS' },
              { value: 'email', label: 'Email' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTemplateFilter(filter.value)}
                style={{
                  padding: '0.5rem 1rem',
                  background: templateFilter === filter.value ? colors.accent : colors.bgInput,
                  color: templateFilter === filter.value ? '#fff' : colors.text,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Templates List */}
          {loadingTemplates ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
              Loading templates...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 2rem',
              background: colors.bgInput,
              borderRadius: '12px'
            }}>
              <MessageSquare size={48} color={colors.textMuted} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p style={{ color: colors.textMuted, margin: '0 0 1rem 0' }}>
                {messageTemplates.length === 0
                  ? "No templates yet. Create your first template to save time on messaging."
                  : "No templates match your filter."}
              </p>
              {messageTemplates.length === 0 && (
                <button
                  onClick={() => openTemplateModal()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: colors.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Create First Template
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Group by category */}
              {Object.entries(
                filteredTemplates.reduce((acc, template) => {
                  const cat = template.category || 'Uncategorized';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(template);
                  return acc;
                }, {})
              ).map(([category, templates]) => (
                <div key={category}>
                  <h3 style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.75rem 0'
                  }}>
                    {category}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {templates.map(template => (
                      <div
                        key={template.id}
                        style={{
                          background: colors.bgInput,
                          borderRadius: '10px',
                          padding: '1rem 1.25rem',
                          border: `1px solid ${colors.border}`
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: template.channel === 'sms' ? '#10b98120' : '#3b82f620',
                              color: template.channel === 'sms' ? '#10b981' : '#3b82f6',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>
                              {template.channel}
                            </span>
                            <span style={{ fontWeight: '600', color: colors.text }}>
                              {template.name}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => openTemplateModal(template)}
                              style={{
                                padding: '0.4rem',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: colors.textMuted
                              }}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => deleteTemplate(template)}
                              style={{
                                padding: '0.4rem',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: colors.danger || '#ef4444'
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: colors.textMuted,
                          whiteSpace: 'pre-wrap',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {template.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Variables Reference */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: colors.bgInput,
            borderRadius: '12px'
          }}>
            <h3 style={{ fontSize: '1rem', margin: '0 0 1rem 0', color: colors.text }}>
              Available Variables
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Use these placeholders in your templates. They'll be replaced with actual values when you send a message.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '0.75rem'
            }}>
              {templateVariables.map(v => (
                <div key={v.var} style={{
                  padding: '0.5rem 0.75rem',
                  background: colors.bgCard,
                  borderRadius: '6px',
                  fontSize: '0.85rem'
                }}>
                  <code style={{ color: colors.accent, fontWeight: '600' }}>{v.var}</code>
                  <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {v.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: colors.textMuted
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* Channel Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Type
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['sms', 'email'].map(channel => (
                    <button
                      key={channel}
                      onClick={() => setTemplateForm(prev => ({ ...prev, channel }))}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: templateForm.channel === channel ? colors.accent : colors.bgInput,
                        color: templateForm.channel === channel ? '#fff' : colors.text,
                        border: `2px solid ${templateForm.channel === channel ? colors.accent : colors.border}`,
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        textTransform: 'uppercase'
                      }}
                    >
                      {channel}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Category
                </label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select a category</option>
                  {templateCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., On My Way, Quote Follow-up"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Message */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Message
                </label>
                <textarea
                  value={templateForm.message}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={templateForm.channel === 'sms'
                    ? "Hi {customer_name}, this is your tech from {company_name}..."
                    : "Hi {customer_name},\n\nThank you for your business!\n\nBest regards,\n{company_name}"
                  }
                  rows={templateForm.channel === 'email' ? 8 : 4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    resize: 'vertical',
                    lineHeight: 1.5
                  }}
                />
                {templateForm.channel === 'sms' && (
                  <div style={{
                    fontSize: '0.8rem',
                    color: templateForm.message.length > 160 ? (colors.danger || '#ef4444') : colors.textMuted,
                    marginTop: '0.5rem'
                  }}>
                    {templateForm.message.length} characters
                    {templateForm.message.length > 160 && ' (will be sent as multiple messages)'}
                  </div>
                )}
              </div>

              {/* Quick Insert Variables */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '500' }}>
                  Insert Variable
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {templateVariables.map(v => (
                    <button
                      key={v.var}
                      onClick={() => insertVariable(v.var)}
                      style={{
                        padding: '0.4rem 0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontFamily: 'monospace'
                      }}
                    >
                      {v.var}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem'
            }}>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.bgInput,
                  color: colors.text,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={savingTemplate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: savingTemplate ? colors.bgInput : colors.accent,
                  color: savingTemplate ? colors.textMuted : '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  cursor: savingTemplate ? 'not-allowed' : 'pointer'
                }}
              >
                <Save size={18} />
                {savingTemplate ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '2rem',
          border: `1px solid ${colors.border}`,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <PaymentSettings companyId={companyId} />
        </div>
      )}

      {activeTab === 'subscription' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            border: `1px solid ${colors.border}`
          }}>
            <SubscriptionSettings companyId={companyId} />
          </div>

          {/* Per-User Messaging Addon */}
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            border: `1px solid ${colors.border}`
          }}>
            <MessagingSettings
              userId={user?.id}
              companyId={companyId}
              userRole={userRole}
              colors={colors}
            />
          </div>
        </div>
      )}

      {activeTab === 'appearance' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Appearance
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Customize how Work Van looks for you
          </p>

          {/* Dark/Light Mode Toggle */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Color Mode
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Choose between dark and light themes
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '1rem'
            }}>
              {/* Dark Mode */}
              <button
                onClick={() => toggleMode()}
                style={{
                  padding: '1.5rem',
                  background: isDark ? `${colors.accent}20` : colors.bgCard,
                  border: isDark ? `2px solid ${colors.accent}` : `2px solid ${colors.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}
              >
                <div style={{
                  width: '80px',
                  height: '50px',
                  background: colors.bg,
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Moon size={24} color={colors.textMuted} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                    Dark Mode
                  </div>
                  <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                    Easier on the eyes
                  </div>
                </div>
                {isDark && (
                  <div style={{
                    padding: '0.35rem 0.75rem',
                    background: colors.accent,
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#fff'
                  }}>
                    Active
                  </div>
                )}
              </button>

              {/* Light Mode */}
              <button
                onClick={() => toggleMode()}
                style={{
                  padding: '1.5rem',
                  background: !isDark ? `${colors.accent}20` : colors.bgCard,
                  border: !isDark ? `2px solid ${colors.accent}` : `2px solid ${colors.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}
              >
                <div style={{
                  width: '80px',
                  height: '50px',
                  background: isDark ? '#f5f5f5' : colors.bg,
                  borderRadius: '8px',
                  border: isDark ? '1px solid #e0e0e0' : `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Sun size={24} color={isDark ? '#f59e0b' : colors.warning} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                    Light Mode
                  </div>
                  <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                    Classic bright look
                  </div>
                </div>
                {!isDark && (
                  <div style={{
                    padding: '0.35rem 0.75rem',
                    background: colors.accent,
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#fff'
                  }}>
                    Active
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Accent Color */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Accent Color
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Choose your brand color for buttons and highlights
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(8, 1fr)',
              gap: '0.75rem'
            }}>
              {accentColors.map((colorOption) => (
                <button
                  key={colorOption.name}
                  onClick={() => setAccentColor(colorOption.color)}
                  title={colorOption.name.charAt(0).toUpperCase() + colorOption.name.slice(1)}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    minHeight: '44px',
                    background: colorOption.gradient,
                    border: theme.accentColor === colorOption.color
                      ? '3px solid #fff'
                      : '3px solid transparent',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    boxShadow: theme.accentColor === colorOption.color
                      ? `0 0 0 2px ${colorOption.color}`
                      : 'none',
                    transition: 'transform 0.15s, box-shadow 0.15s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
              ))}
            </div>

            {/* Current color preview */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: colors.accentGradient,
                borderRadius: '10px',
                flexShrink: 0
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                  Current: {theme.accentColorName?.charAt(0).toUpperCase() + theme.accentColorName?.slice(1) || 'Orange'}
                </div>
                <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                  {colors.accent}
                </div>
              </div>
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.accentGradient,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                Preview Button
              </button>
            </div>
          </div>

          {/* Business Branding for Quotes & Invoices */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginTop: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 0.5rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Palette size={20} />
              Business Branding
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Customize colors for your quotes, invoices, and customer-facing documents
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '1.5rem'
            }}>
              {/* Primary Color */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Primary Color
                </label>
                <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Headers, buttons, and key highlights
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={companySettings.brand_primary_color}
                    onChange={(e) => {
                      setCompanySettings(prev => ({ ...prev, brand_primary_color: e.target.value }));
                      saveCompanySettings({ brand_primary_color: e.target.value });
                    }}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  />
                  <input
                    type="text"
                    value={companySettings.brand_primary_color}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanySettings(prev => ({ ...prev, brand_primary_color: val }));
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        saveCompanySettings({ brand_primary_color: val });
                      }
                    }}
                    placeholder="#4ecca3"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Secondary Color
                </label>
                <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Accents, badges, and callouts
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={companySettings.brand_secondary_color}
                    onChange={(e) => {
                      setCompanySettings(prev => ({ ...prev, brand_secondary_color: e.target.value }));
                      saveCompanySettings({ brand_secondary_color: e.target.value });
                    }}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  />
                  <input
                    type="text"
                    value={companySettings.brand_secondary_color}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanySettings(prev => ({ ...prev, brand_secondary_color: val }));
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        saveCompanySettings({ brand_secondary_color: val });
                      }
                    }}
                    placeholder="#F05A28"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Link/Accent Color
                </label>
                <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Links and interactive elements
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={companySettings.brand_accent_color}
                    onChange={(e) => {
                      setCompanySettings(prev => ({ ...prev, brand_accent_color: e.target.value }));
                      saveCompanySettings({ brand_accent_color: e.target.value });
                    }}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  />
                  <input
                    type="text"
                    value={companySettings.brand_accent_color}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanySettings(prev => ({ ...prev, brand_accent_color: val }));
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        saveCompanySettings({ brand_accent_color: val });
                      }
                    }}
                    placeholder="#3498db"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </div>

              {/* Text Color */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Text Color
                </label>
                <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Main body text on documents
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={companySettings.brand_text_color}
                    onChange={(e) => {
                      setCompanySettings(prev => ({ ...prev, brand_text_color: e.target.value }));
                      saveCompanySettings({ brand_text_color: e.target.value });
                    }}
                    style={{
                      width: '50px',
                      height: '50px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  />
                  <input
                    type="text"
                    value={companySettings.brand_text_color}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanySettings(prev => ({ ...prev, brand_text_color: val }));
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        saveCompanySettings({ brand_text_color: val });
                      }
                    }}
                    placeholder="#333333"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: '#ffffff',
              borderRadius: '12px',
              border: `1px solid ${colors.border}`
            }}>
              <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Preview - Quote/Invoice Header
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: companySettings.brand_primary_color,
                    marginBottom: '0.25rem'
                  }}>
                    {companySettings.quote_company_name || 'Your Company Name'}
                  </div>
                  <div style={{ color: companySettings.brand_text_color, fontSize: '0.9rem' }}>
                    {companySettings.quote_company_phone || '(555) 123-4567'}
                  </div>
                </div>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: companySettings.brand_secondary_color,
                  color: '#fff',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}>
                  QUOTE #12345
                </div>
              </div>
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: `2px solid ${companySettings.brand_primary_color}`
              }}>
                <div style={{ color: companySettings.brand_text_color, marginBottom: '0.5rem' }}>
                  Sample line item description
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: companySettings.brand_accent_color, textDecoration: 'underline', cursor: 'pointer' }}>
                    View details
                  </span>
                  <span style={{ fontWeight: 'bold', color: companySettings.brand_primary_color }}>
                    $250.00
                  </span>
                </div>
              </div>
            </div>

            {/* Reset to defaults */}
            <button
              onClick={() => {
                const defaults = {
                  brand_primary_color: '#4ecca3',
                  brand_secondary_color: '#F05A28',
                  brand_accent_color: '#3498db',
                  brand_text_color: '#333333'
                };
                setCompanySettings(prev => ({ ...prev, ...defaults }));
                saveCompanySettings(defaults);
                showSuccess('Colors reset to defaults');
              }}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Reset to Default Colors
            </button>
          </div>

          {/* Default View Settings */}
          <div style={{
            background: colors.bgInput,
            padding: isMobile ? '1rem' : '1.5rem',
            borderRadius: '12px',
            marginTop: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text
            }}>
              Default View on Login
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Choose which page opens when you log in
            </p>

            <select
              value={companySettings.default_view}
              onChange={(e) => saveCompanySettings({ default_view: e.target.value })}
              disabled={saving}
              style={{
                width: '100%',
                maxWidth: isMobile ? '100%' : '400px',
                padding: '0.875rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                minHeight: '44px'
              }}
            >
              {availableMenuItems.map(item => (
                <option key={item.id} value={item.id}>
                  {item.icon} {item.label}
                </option>
              ))}
            </select>
          </div>

          {/* Smart Assistant Toggle */}
          <div style={{
            background: colors.bgInput,
            padding: isMobile ? '1rem' : '1.5rem',
            borderRadius: '12px',
            marginTop: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Bell size={20} />
              Smart Assistant
            </h3>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Get helpful alerts 15 minutes before your scheduled job end time with info about your next job,
              navigation, and quick-text options
            </p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={companySettings.smart_assistant_enabled}
                onChange={(e) => saveCompanySettings({ smart_assistant_enabled: e.target.checked })}
                disabled={saving}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              />
              <span style={{ color: colors.text }}>
                Enable Smart Assistant alerts
              </span>
            </label>
          </div>

          {/* Menu Order */}
          <div style={{
            background: colors.bgInput,
            padding: isMobile ? '1rem' : '1.5rem',
            borderRadius: '12px',
            marginTop: '2rem'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? '1rem' : '0',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                margin: 0,
                color: colors.text
              }}>
                Customize Menu Order
              </h3>
              <button
                onClick={resetMenuOrder}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  background: colors.bgHover,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  fontSize: '0.85rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  minHeight: '44px'
                }}
              >
                Reset to Default
              </button>
            </div>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Rearrange your menu items to match your workflow
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              {getCurrentMenuOrder().map((itemId, index) => {
                const item = availableMenuItems.find(m => m.id === itemId);
                if (!item) return null;

                return (
                  <div
                    key={itemId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '0.5rem' : '1rem',
                      padding: isMobile ? '0.75rem' : '0.875rem 1rem',
                      background: colors.bgCard,
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      minWidth: 0
                    }}
                  >
                    {!isMobile && <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>☰</span>}
                    <span style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', flexShrink: 0 }}>{item.icon}</span>
                    <span style={{
                      flex: 1,
                      color: colors.text,
                      fontWeight: '500',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.label}
                    </span>
                    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                      <button
                        onClick={() => moveMenuItem(index, 'up')}
                        disabled={index === 0 || saving}
                        style={{
                          padding: isMobile ? '0.4rem' : '0.5rem',
                          background: index === 0 ? colors.bgInput : colors.bgHover,
                          border: 'none',
                          borderRadius: '6px',
                          color: index === 0 ? colors.textMuted : colors.text,
                          cursor: index === 0 || saving ? 'not-allowed' : 'pointer',
                          fontSize: isMobile ? '1rem' : '1.25rem',
                          lineHeight: '1',
                          width: isMobile ? '32px' : '36px',
                          height: isMobile ? '32px' : '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveMenuItem(index, 'down')}
                        disabled={index === getCurrentMenuOrder().length - 1 || saving}
                        style={{
                          padding: isMobile ? '0.4rem' : '0.5rem',
                          background: index === getCurrentMenuOrder().length - 1 ? colors.bgInput : colors.bgHover,
                          border: 'none',
                          borderRadius: '6px',
                          color: index === getCurrentMenuOrder().length - 1 ? colors.textMuted : colors.text,
                          cursor: index === getCurrentMenuOrder().length - 1 || saving ? 'not-allowed' : 'pointer',
                          fontSize: isMobile ? '1rem' : '1.25rem',
                          lineHeight: '1',
                          width: isMobile ? '32px' : '36px',
                          height: isMobile ? '32px' : '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ↓
                      </button>
                      {itemId !== 'settings' && (
                        <button
                          onClick={() => removeMenuItem(itemId)}
                          disabled={saving}
                          title="Remove from menu"
                          style={{
                            padding: isMobile ? '0.4rem' : '0.5rem',
                            background: colors.bgHover,
                            border: 'none',
                            borderRadius: '6px',
                            color: colors.error,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '1rem' : '1.25rem',
                            lineHeight: '1',
                            width: isMobile ? '32px' : '36px',
                            height: isMobile ? '32px' : '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Hidden/Removed Items */}
            {getHiddenMenuItems().length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{
                  fontSize: '1rem',
                  margin: '0 0 0.75rem 0',
                  color: colors.textMuted,
                  fontWeight: '600'
                }}>
                  Removed Items
                </h4>
                <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.85rem' }}>
                  Click + to restore an item to your menu
                </p>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {getHiddenMenuItems().map(item => (
                    <button
                      key={item.id}
                      onClick={() => restoreMenuItem(item.id)}
                      disabled={saving}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: colors.bgCard,
                        border: `1px dashed ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.textMuted,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      <span style={{ color: colors.success, fontWeight: 'bold' }}>+</span>
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'referral' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <h2 style={{ fontSize: '1.75rem', margin: '0 0 0.5rem 0', color: colors.text }}>
              Earn With WorkVan
            </h2>
            <p style={{ color: colors.textMuted, fontSize: '1rem', maxWidth: '500px', margin: '0 auto' }}>
              Refer other businesses to WorkVan and earn 10% of their subscription fees — forever!
            </p>
          </div>

          {/* Referral Link Box */}
          <div style={{
            background: colors.accentGradient,
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Your Referral Link
            </div>
            <div style={{
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              color: 'rgba(255,255,255,0.9)',
              fontSize: isMobile ? '0.85rem' : '1rem',
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              workvanapp.com?ref={companyId ? companyId.substring(0, 8).toUpperCase() : '...'}
            </div>
            <button
              onClick={() => {
                const code = companyId?.substring(0, 8).toUpperCase();
                const url = `https://workvanapp.com?ref=${code}`;
                navigator.clipboard.writeText(url);
                showSuccess('Referral link copied!');
              }}
              style={{
                padding: '0.75rem 2rem',
                background: '#fff',
                border: 'none',
                borderRadius: '8px',
                color: colors.accent,
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Copy Link
            </button>
          </div>

          {/* Referral Earnings Dashboard - only show if they have credits or referred companies */}
          {(referralCredits.length > 0 || referredCompanies.length > 0) && (
            <div style={{
              background: colors.bgInput,
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ fontSize: '1.25rem', margin: '0 0 1.5rem 0', color: colors.text }}>
                Your Referral Earnings
              </h3>
              {loadingReferrals ? (
                <div style={{ textAlign: 'center', color: colors.textMuted, padding: '1rem' }}>Loading...</div>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{
                      padding: '1rem',
                      background: colors.bgCard,
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '1.5rem' }}>
                        ${(referralCredits.reduce((sum, c) => sum + c.credit_amount_cents, 0) / 100).toFixed(2)}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.25rem' }}>All-Time</div>
                    </div>
                    <div style={{
                      padding: '1rem',
                      background: colors.bgCard,
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '1.5rem' }}>
                        ${(() => {
                          const now = new Date();
                          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
                          return (referralCredits
                            .filter(c => c.created_at >= monthStart)
                            .reduce((sum, c) => sum + c.credit_amount_cents, 0) / 100).toFixed(2);
                        })()}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.25rem' }}>This Month</div>
                    </div>
                    <div style={{
                      padding: '1rem',
                      background: colors.bgCard,
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      ...(isMobile ? { gridColumn: '1 / -1' } : {})
                    }}>
                      <div style={{ color: colors.accent, fontWeight: 'bold', fontSize: '1.5rem' }}>
                        {referredCompanies.length}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.25rem' }}>Active Referrals</div>
                    </div>
                  </div>

                  {/* Referred accounts list */}
                  {referredCompanies.length > 0 && (
                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem' }}>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Referred Accounts
                      </div>
                      {referredCompanies.map(rc => {
                        const earned = referralCredits
                          .filter(c => c.referred_company_id === rc.id)
                          .reduce((sum, c) => sum + c.credit_amount_cents, 0);
                        return (
                          <div key={rc.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: `1px solid ${colors.border}`
                          }}>
                            <span style={{ color: colors.text }}>{rc.name || 'Unnamed Business'}</span>
                            <span style={{ color: '#22c55e', fontWeight: '600' }}>
                              ${(earned / 100).toFixed(2)} earned
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* How It Works */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1.5rem 0', color: colors.text }}>
              How It Works
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '1.5rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: colors.accent,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#fff'
                }}>1</div>
                <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.5rem' }}>Share Your Code</div>
                <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                  Send your referral link to other service businesses
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: colors.accent,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#fff'
                }}>2</div>
                <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.5rem' }}>They Sign Up</div>
                <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                  When they subscribe using your link, you're connected
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: colors.accent,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#fff'
                }}>3</div>
                <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.5rem' }}>Earn Forever</div>
                <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                  Get 10% of their subscription as credit each month
                </div>
              </div>
            </div>
          </div>

          {/* Earnings Example */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem 0', color: colors.text }}>
              Potential Earnings
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '1rem'
            }}>
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Refer 1 business
                </div>
                <div style={{ color: colors.accent, fontWeight: 'bold', fontSize: '1.25rem' }}>
                  $7.50/month forever*
                </div>
              </div>
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Refer 10 businesses
                </div>
                <div style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '1.25rem' }}>
                  $75/month forever*
                </div>
              </div>
            </div>
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '1rem', marginBottom: 0 }}>
              *Based on avg. subscription of $75/mo. Credits apply to your monthly bill.
            </p>
          </div>

          {/* Fine Print */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
              Program Details
            </h3>
            <ul style={{ color: colors.textMuted, fontSize: '0.9rem', margin: 0, paddingLeft: '1.25rem', lineHeight: 1.8 }}>
              <li>Earn 10% of each referred account's subscription fee</li>
              <li><strong>WorkVan users:</strong> Earnings applied as account credit to your monthly invoice</li>
              <li><strong>Non-users:</strong> Get paid directly (contact us to set up)</li>
              <li>Earnings continue as long as referred accounts remain active and in good standing</li>
              <li>No limit on the number of referrals</li>
              <li>Referred businesses must be new to WorkVan</li>
              <li>Program terms subject to change with 30 days notice</li>
            </ul>
          </div>
        </div>
      )}

      {/* Chemicals Tab */}
      {activeTab === 'chemicals' && (
        <ChemicalManagement companyId={companyId} />
      )}

      {/* Quote Visits Tab */}
      {activeTab === 'quotevisits' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Quote Visit Settings
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Charge a fee for quote visits that gets waived when customers agree to work
          </p>

          {/* Enable Quote Fee Toggle */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <div>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Enable Quote Visit Fees
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Collect a fee when the quote visit is complete
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ quote_fee_enabled: !companySettings.quote_fee_enabled })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.quote_fee_enabled ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.quote_fee_enabled ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.quote_fee_enabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {/* Default Fee Amount */}
            {companySettings.quote_fee_enabled && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.85rem'
                }}>
                  Default Quote Visit Fee
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: colors.text, fontSize: '1.25rem' }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={companySettings.default_quote_fee_amount}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setCompanySettings(prev => ({ ...prev, default_quote_fee_amount: value }));
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      saveCompanySettings({ default_quote_fee_amount: value }, { silent: true });
                    }}
                    disabled={saving}
                    style={{
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      width: '120px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* How It Works */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px'
          }}>
            <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
              How It Works
            </h3>
            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: colors.accent,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>1</div>
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Create Quote Visit
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                    When scheduling a quote visit, the fee amount is set (adjustable per visit)
                  </div>
                </div>
              </div>

              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#22c55e',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>2</div>
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Customer Agrees = Fee Waived
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                    If the customer agrees to work, the fee is waived and credited to their future invoice
                  </div>
                </div>
              </div>

              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: colors.warning,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>3</div>
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Customer Declines = Fee Collected
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                    If the customer doesn't agree, collect the quote visit fee via card, cash, or check
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: '0 0 1rem 0', color: colors.text }}>
            Inventory Settings
          </h2>
          <p style={{ color: colors.textMuted, marginBottom: '2rem' }}>
            Track materials and parts on your truck for job planning
          </p>

          {/* Enable Inventory Toggle */}
          <div style={{
            background: colors.bgInput,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: colors.bgCard,
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              <div>
                <div style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  marginBottom: '0.25rem'
                }}>
                  Enable Inventory Tracking
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Track materials on your truck and plan jobs with materials
                </div>
              </div>
              <div
                onClick={() => !saving && saveCompanySettings({ inventory_enabled: !companySettings.inventory_enabled })}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: companySettings.inventory_enabled ? colors.accent : colors.bgHover,
                  position: 'relative',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  border: `1px solid ${companySettings.inventory_enabled ? colors.accent : colors.border}`
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: companySettings.inventory_enabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
            </label>

            {/* Inventory Options - only show when enabled */}
            {companySettings.inventory_enabled && (
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: colors.bgCard,
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  <div>
                    <div style={{
                      color: colors.text,
                      fontWeight: 'bold',
                      marginBottom: '0.25rem'
                    }}>
                      Auto-Deduct on Job Completion
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      Automatically subtract materials from inventory when jobs are completed
                    </div>
                  </div>
                  <div
                    onClick={() => !saving && saveCompanySettings({ inventory_auto_deduct: !companySettings.inventory_auto_deduct })}
                    style={{
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      background: companySettings.inventory_auto_deduct ? colors.accent : colors.bgHover,
                      position: 'relative',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      border: `1px solid ${companySettings.inventory_auto_deduct ? colors.accent : colors.border}`
                    }}
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: '2px',
                      left: companySettings.inventory_auto_deduct ? '27px' : '3px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }} />
                  </div>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: colors.bgCard,
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  <div>
                    <div style={{
                      color: colors.text,
                      fontWeight: 'bold',
                      marginBottom: '0.25rem'
                    }}>
                      Low Stock Alerts
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      Show warnings when items fall below their reorder point
                    </div>
                  </div>
                  <div
                    onClick={() => !saving && saveCompanySettings({ inventory_low_stock_alerts: !companySettings.inventory_low_stock_alerts })}
                    style={{
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      background: companySettings.inventory_low_stock_alerts ? colors.accent : colors.bgHover,
                      position: 'relative',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      border: `1px solid ${companySettings.inventory_low_stock_alerts ? colors.accent : colors.border}`
                    }}
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: '2px',
                      left: companySettings.inventory_low_stock_alerts ? '27px' : '3px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }} />
                  </div>
                </label>

                {/* Inventory Mode Selector */}
                <div style={{
                  padding: '1rem',
                  background: colors.bgCard,
                  borderRadius: '8px'
                }}>
                  <div style={{
                    color: colors.text,
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                  }}>
                    Inventory Mode
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '1rem' }}>
                    Choose between single location or multi-location inventory tracking
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => saveCompanySettings({ inventory_mode: 'simple' })}
                      disabled={saving}
                      style={{
                        flex: 1,
                        minWidth: '140px',
                        padding: '0.75rem 1rem',
                        background: companySettings.inventory_mode === 'simple' ? colors.accent : colors.bgInput,
                        border: `1px solid ${companySettings.inventory_mode === 'simple' ? colors.accent : colors.border}`,
                        borderRadius: '8px',
                        color: companySettings.inventory_mode === 'simple' ? '#fff' : colors.text,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: companySettings.inventory_mode === 'simple' ? 'bold' : 'normal'
                      }}
                    >
                      Simple (Single Location)
                    </button>
                    <button
                      onClick={() => saveCompanySettings({ inventory_mode: 'multi_location' })}
                      disabled={saving}
                      style={{
                        flex: 1,
                        minWidth: '140px',
                        padding: '0.75rem 1rem',
                        background: companySettings.inventory_mode === 'multi_location' ? colors.accent : colors.bgInput,
                        border: `1px solid ${companySettings.inventory_mode === 'multi_location' ? colors.accent : colors.border}`,
                        borderRadius: '8px',
                        color: companySettings.inventory_mode === 'multi_location' ? '#fff' : colors.text,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: companySettings.inventory_mode === 'multi_location' ? 'bold' : 'normal'
                      }}
                    >
                      Multi-Location
                    </button>
                  </div>
                  {companySettings.inventory_mode === 'multi_location' && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: `${colors.accent}15`,
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: colors.accent
                    }}>
                      Track inventory across warehouses, offices, and trucks. Transfer stock between locations.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Links - only show when enabled */}
          {companySettings.inventory_enabled && (
            <div style={{
              background: colors.bgInput,
              padding: '1.5rem',
              borderRadius: '12px'
            }}>
              <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
                Manage Inventory
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : companySettings.inventory_mode === 'multi_location' ? '1fr 1fr 1fr' : '1fr 1fr',
                gap: '1rem'
              }}>
                <button
                  onClick={() => onNavigate && onNavigate('inventory')}
                  style={{
                    padding: '1rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.2s'
                  }}
                >
                  <Package size={24} color={colors.accent} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold' }}>Inventory Items</div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>Manage stock levels</div>
                  </div>
                </button>

                {companySettings.inventory_mode === 'multi_location' && (
                  <button
                    onClick={() => onNavigate && onNavigate('locations')}
                    style={{
                      padding: '1rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Warehouse size={24} color={colors.accent} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 'bold' }}>Locations</div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>Warehouses & trucks</div>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => onNavigate && onNavigate('job-templates')}
                  style={{
                    padding: '1rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.2s'
                  }}
                >
                  <FileText size={24} color={colors.accent} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold' }}>Job Templates</div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>Pre-built job plans</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Alerts & Notifications */}
          {companySettings.inventory_enabled && (
            <div style={{
              background: colors.bgInput,
              padding: '1.5rem',
              borderRadius: '12px',
              marginTop: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1.1rem', margin: '0 0 1rem 0', color: colors.text }}>
                Alerts & Notifications
              </h3>

              {/* Alert Thresholds */}
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.75rem' }}>
                  Default Alert Thresholds
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Multiplier of each item's reorder point. Warning at 1.5x means alert when stock hits 150% of reorder point.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: colors.warning, marginBottom: '0.25rem', fontWeight: 'bold' }}>
                      Warning (yellow)
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={companySettings.inventory_default_warning_threshold}
                      onChange={(e) => saveCompanySettings({ inventory_default_warning_threshold: parseFloat(e.target.value) || 1.5 })}
                      style={{ width: '100%', padding: '0.5rem', background: colors.bgInput, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.text }}
                    />
                    <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                      e.g. 1.5 = alert at 150% of reorder point
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: colors.error, marginBottom: '0.25rem', fontWeight: 'bold' }}>
                      Critical (red)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={companySettings.inventory_default_critical_threshold}
                      onChange={(e) => saveCompanySettings({ inventory_default_critical_threshold: parseFloat(e.target.value) || 0.5 })}
                      style={{ width: '100%', padding: '0.5rem', background: colors.bgInput, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.text }}
                    />
                    <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                      e.g. 0.5 = alert at 50% of reorder point
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Digest */}
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Email Digest
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  Receive a summary of low stock items by email
                </div>
                <select
                  value={companySettings.inventory_alert_email_digest}
                  onChange={(e) => saveCompanySettings({ inventory_alert_email_digest: e.target.value })}
                  disabled={saving}
                  style={{ width: '100%', padding: '0.5rem', background: colors.bgInput, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.text, marginBottom: '0.75rem' }}
                >
                  <option value="none">Disabled</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Monday)</option>
                </select>

                {companySettings.inventory_alert_email_digest !== 'none' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                      Send to (comma-separated emails)
                    </label>
                    <input
                      type="text"
                      value={(companySettings.inventory_digest_emails || []).join(', ')}
                      onChange={(e) => {
                        const emails = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        saveCompanySettings({ inventory_digest_emails: emails });
                      }}
                      placeholder="office@company.com, manager@company.com"
                      style={{ width: '100%', padding: '0.5rem', background: colors.bgInput, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.text }}
                    />
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch('/api/inventory-digest', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ companyId, force: true })
                          });
                          const result = await res.json();
                          if (result.sent) showSuccess(`Digest sent to ${result.recipients} recipient(s)`);
                          else showAlert(result.reason || 'Nothing to send');
                        } catch (err) {
                          showError('Failed to send digest');
                        }
                      }}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.4rem 0.75rem',
                        background: 'transparent',
                        border: `1px solid ${colors.accent}`,
                        borderRadius: '6px',
                        color: colors.accent,
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      Send Test Digest Now
                    </button>
                  </div>
                )}
              </div>

              {/* Monthly Budget */}
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Monthly Materials Budget
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  Set a target budget for materials spending. Track actual vs budget in reports.
                </div>
                <div style={{ position: 'relative', maxWidth: '200px' }}>
                  <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={companySettings.inventory_monthly_budget}
                    onChange={(e) => saveCompanySettings({ inventory_monthly_budget: parseFloat(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 1.75rem', background: colors.bgInput, border: `1px solid ${colors.border}`, borderRadius: '6px', color: colors.text }}
                  />
                </div>
              </div>

              {/* SMS Critical Alerts */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    SMS Critical Alerts
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Send SMS when items hit critical level (out of stock)
                  </div>
                </div>
                <div
                  onClick={() => !saving && saveCompanySettings({ inventory_sms_critical_alerts: !companySettings.inventory_sms_critical_alerts })}
                  style={{
                    width: '52px',
                    height: '28px',
                    borderRadius: '14px',
                    background: companySettings.inventory_sms_critical_alerts ? colors.accent : colors.bgHover,
                    position: 'relative',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                    flexShrink: 0,
                    border: `1px solid ${companySettings.inventory_sms_critical_alerts ? colors.accent : colors.border}`
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: '#fff',
                    position: 'absolute',
                    top: '2px',
                    left: companySettings.inventory_sms_critical_alerts ? '27px' : '3px',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </label>
            </div>
          )}

          {/* Info Box */}
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: `${colors.accent}20`,
            border: `1px solid ${colors.accent}50`,
            borderRadius: '8px',
            color: colors.accent,
            fontSize: '0.9rem'
          }}>
            {companySettings.inventory_mode === 'multi_location' ? (
              <>
                <strong>Multi-Location Mode:</strong> Track inventory across warehouses, offices, and trucks. Transfer stock between locations and monitor stock levels per location.
              </>
            ) : (
              <>
                <strong>Simple Mode:</strong> Track what's on your truck, plan jobs with materials, and get low stock warnings. Enable Multi-Location mode above to track inventory across multiple warehouses and trucks.
              </>
            )}
          </div>
        </div>
      )}

      {/* Logo Crop Modal */}
      {showLogoCropModal && cropImageSrc && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => setShowLogoCropModal(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              padding: '1.5rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '1.25rem',
              margin: '0 0 1rem 0',
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Crop size={24} />
              Crop & Resize Logo
            </h3>

            <p style={{
              color: colors.textMuted,
              fontSize: '0.9rem',
              marginBottom: '1rem'
            }}>
              Drag to position, use +/- to zoom. The logo will be cropped to a square.
            </p>

            {/* Crop Preview Container */}
            <div
              style={{
                width: '100%',
                aspectRatio: '1',
                background: '#f0f0f0',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative',
                cursor: isDragging ? 'grabbing' : 'grab',
                marginBottom: '1rem',
                border: `2px solid ${colors.border}`
              }}
              onMouseDown={handleCropMouseDown}
              onMouseMove={handleCropMouseMove}
              onMouseUp={handleCropMouseUp}
              onMouseLeave={handleCropMouseUp}
              onTouchStart={handleCropTouchStart}
              onTouchMove={handleCropTouchMove}
              onTouchEnd={handleCropTouchEnd}
            >
              {/* Hidden canvas for processing */}
              <canvas ref={cropCanvasRef} style={{ display: 'none' }} />
              
              {/* Preview image */}
              <img
                ref={cropImageRef}
                src={cropImageSrc}
                alt="Crop preview"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(calc(-50% + ${cropPosition.x}px), calc(-50% + ${cropPosition.y}px)) scale(${cropScale})`,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  touchAction: 'none'
                }}
                draggable={false}
              />

              {/* Center guide lines */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                background: 'rgba(240, 90, 40, 0.5)',
                pointerEvents: 'none'
              }} />
              <div style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '1px',
                background: 'rgba(240, 90, 40, 0.5)',
                pointerEvents: 'none'
              }} />
            </div>

            {/* Zoom Controls */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <button
                onClick={() => handleZoom('out')}
                disabled={cropScale <= 0.1}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  border: `2px solid ${colors.border}`,
                  background: colors.bgInput,
                  color: colors.text,
                  cursor: cropScale <= 0.5 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: cropScale <= 0.5 ? 0.5 : 1
                }}
              >
                <ZoomOut size={20} />
              </button>

              <span style={{
                fontSize: '0.9rem',
                color: colors.textMuted,
                minWidth: '60px',
                textAlign: 'center'
              }}>
                {Math.round(cropScale * 100)}%
              </span>

              <button
                onClick={() => handleZoom('in')}
                disabled={cropScale >= 3}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  border: `2px solid ${colors.border}`,
                  background: colors.bgInput,
                  color: colors.text,
                  cursor: cropScale >= 3 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: cropScale >= 3 ? 0.5 : 1
                }}
              >
                <ZoomIn size={20} />
              </button>

              <button
                onClick={resetCrop}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  border: `2px solid ${colors.border}`,
                  background: colors.bgInput,
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Reset"
              >
                <RotateCcw size={18} />
              </button>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => setShowLogoCropModal(false)}
                disabled={logoUploading}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.bgInput,
                  border: 'none',
                  borderRadius: '10px',
                  color: colors.text,
                  fontWeight: '600',
                  cursor: logoUploading ? 'not-allowed' : 'pointer',
                  opacity: logoUploading ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={cropAndUploadLogo}
                disabled={logoUploading}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: logoUploading ? 'not-allowed' : 'pointer',
                  opacity: logoUploading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {logoUploading ? (
                  <>
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save Logo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
